import { Formatters } from '../shared/lib/formatters';
import { LoadingSpinner } from '../shared/components';
import { emptyState } from '../shared/components/Narrative';
import { kebabHtml, bindKebabs } from '../shared/components/Kebab';
// R114 Day view: pure helpers shared with lib/arrivalsDayView.test.mjs (eodFeed pattern).
import {
  STORE_KEYS, MODES, MODE_LABELS, DAY_WINDOW, isDay, addDays, inWindow, dayToStore, parseMode, parseView,
  dayLabel, longDay, partyLabel, nightsLabel, rowsFor, modeCounts, emptyText, turnStatusLabel, inspectionLabel, generateDays,
  restoreDay, collapseToToday, dateEntryGuard, staleAfterWrite, needsRefetch,
  viewFreshness, noteLock, paTileText, paRowsFor, paEmptyText, autoSyncCovers, datesChangedTip,
} from '../../../lib/arrivalsDayView.mjs';
import type { DayMode, DayView, DayPayload, DayArrival, DayDeparture, TurnLite, OtherInspection, DaySelfHeal } from '../../../lib/arrivalsDayView.mjs';

// Arrivals — pre-check-in verification tracker (slice 1). One row per upcoming
// confirmed reservation with a checklist snapshotted from the global template
// (GET/POST /template); the board (GET /board) computes due-dates and roll-ups
// at read time. Internal ops surface — no guest portal, no financial columns.
const API = '/api/restracker';

const CHANNELS = ['airbnb', 'vrbo', 'booking', 'direct', 'other'];
const TPL_EDIT_ROLES = ['admin', 'director', 'manager', 'remote_ops'];

const PILL: Record<string, [string, string, string]> = {
  verified: ['var(--success-soft)', 'var(--success-text, #166534)', 'Verified'],
  pending: ['var(--bg-subtle)', 'var(--text-muted)', 'Pending'],
  at_risk: ['var(--warning-soft)', 'var(--warning-text)', 'At risk'],
  blocked: ['var(--danger-soft)', 'var(--danger-text)', 'Blocked'],
};

type TplItem = {
  key: string; label: string; description?: string;
  required: 'required' | 'optional'; offset_hours: number; channels: string[] | null;
};

export class ResTrackerPage {
  private container: HTMLElement;
  private board: any = null;
  private template: any = null;
  private days = 14;                          // horizon: 7 / 14 / 30
  private expanded = new Set<string>();       // expanded check ids
  private uploadingItem: string | null = null; // "checkId|itemKey" currently uploading a photo
  // R48 check-level note — drafts live HERE, not in the DOM: draw() rebuilds
  // innerHTML, so unmirrored textarea typing dies on any redraw.
  private noteDrafts = new Map<string, string>(); // check id → unsaved note text
  private noteSaving: string | null = null;       // check id with an in-flight note save
  // R51 Quo: per-check call/text timelines, LAZY-fetched on demand (never on
  // board load — one guest = up to 2 API calls per workspace line). null =
  // not fetched, 'loading' = in flight; absent key = section collapsed.
  private quoData = new Map<string, any>();
  private quoConfigured: boolean | null = null;   // null until /api/quo/status answers
  private generating = false;
  private genNote = '';
  // Template editor working copy — STATE-BACKED: inputs mirror into this array
  // on every keystroke so redraws (board refresh etc.) never lose edits.
  private tplOpen = false;
  private tplItems: TplItem[] = [];
  private tplDirty = false;
  private tplNote = '';
  // R114 Day view (Jackie: check-ins / check-outs for a chosen date). The hub
  // builds a NEW instance on every render and on the 15-min auto-refresh, so
  // view / day / filter live in sessionStorage (every get/set try/catch —
  // private mode must still render). day = null means "follow today" (a tab
  // left open overnight lands on the new today, never yesterday).
  private view: DayView = 'day';
  private day: string | null = null;
  private mode: DayMode = 'both';
  private dayData: DayPayload | null = null;
  private dayLoading = false;
  private dayError = '';
  private daySeq = 0;
  // R114 review: one instance holds BOTH views' data. A write in one view
  // marks the other view's copy stale; switching to a stale view refetches it
  // (the cached copy paints meanwhile with its note boxes locked, so a stale
  // note can never be typed over a newer one). boardLoading = a /board fetch
  // in flight.
  // R114 fix2: staleness carries an invalidation generation (lib
  // viewFreshness) — a failed refetch keeps the copy stale (notes locked +
  // an error bar with Retry), and a fetch that started before a write never
  // marks the view fresh. /board is sequenced like /day.
  private fresh = viewFreshness();
  private boardLoading = false;
  private boardError = '';
  private boardSeq = 0;
  // R114 pre-arrival auto-detect: the KPI filter, "Check Hostaway now", and the
  // admin config block (STATE-BACKED draft, like tplItems) + calibration preview.
  private paOnly = false;                       // board filter: only arrivals whose pre-arrival item is still pending
  private paScanBusy = false;
  private paScanNote = '';
  private paOpen = false;
  private paDraft: Record<string, any> | null = null;
  private paSaved: any = null;                  // last GET /prearrival payload
  private paDirty = false;
  private paSaving = false;
  private paNote = '';
  private paPreview: any = null;                // GET /prearrival/preview state
  private paPolling = false;

  constructor(container: HTMLElement) {
    this.container = container;
    this.view = parseView(this.readStore(STORE_KEYS.view));
    this.mode = parseMode(this.readStore(STORE_KEYS.mode));
    // R114 review: never collapse the stored day against the DEVICE date (it can
    // be a day off BUSINESS_TZ — a remount used to drop "tomorrow" back to
    // today). fetchDay collapses it against payload.today instead.
    this.day = restoreDay(this.readStore(STORE_KEYS.day), this.localToday());
    // one-shot: the Calendar's "Open Arrivals" opens that stay's checklist
    const focus = this.readStore(STORE_KEYS.focus);
    if (focus) { this.expanded.add(focus); this.writeStore(STORE_KEYS.focus, null); }
  }
  private readStore(k: string): string | null { try { return sessionStorage.getItem(k); } catch { return null; } }
  private writeStore(k: string, v: string | null): void {
    try { if (v == null) sessionStorage.removeItem(k); else sessionStorage.setItem(k, v); } catch { /* private mode */ }
  }

  async render(): Promise<void> {
    this.container.innerHTML = `<div class="page-content"><div id="rt-body"><div style="padding:40px; text-align:center;">${LoadingSpinner.render()}</div></div></div>`;
    // R51: one cheap status probe decides whether Quo surfaces render at all.
    if (this.quoConfigured === null) {
      fetch('/api/quo/status').then(r => r.ok ? r.json() : null).then(j => {
        this.quoConfigured = !!(j && j.configured);
        // Review fix: drawing before the board load painted the "unavailable"
        // empty state over the spinner. Only redraw once real data exists.
        if (this.quoConfigured && (this.board || this.dayData)) this.draw();
      }).catch(() => { this.quoConfigured = false; });
    }
    await this.load();
  }

  // R51 Quo: collapsible unified call/text history on the arrival card.
  private quoBlock(c: any): string {
    if (!this.quoConfigured || !c.guest_phone) return '';
    const id = String(c.id);
    const d = this.quoData.get(id);
    const head = `<button class="rt-quo-toggle" data-quo-toggle="${this.esc(id)}" data-quo-phone="${this.esc(c.guest_phone)}" style="background:none; border:none; cursor:pointer; color:var(--accent); font-size:12px; font-weight:600; padding:4px 0;">${d ? '▾' : '▸'} 📞 Calls &amp; texts (Quo)</button>`;
    if (!d) return `<div style="padding:2px 14px;">${head}</div>`;
    if (d === 'loading') return `<div style="padding:2px 14px;">${head}<div class="rt-faint" style="padding:4px 0 8px;">Loading…</div></div>`;
    if (d.error || d.configured === false) return `<div style="padding:2px 14px;">${head}<div class="rt-faint" style="padding:4px 0 8px;">Couldn't load Quo activity — try again.</div></div>`;
    const fmtAt = (s: any) => { const dt = new Date(String(s || '')); return isNaN(dt.getTime()) ? '' : `${dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} ${dt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`; };
    const rows = [
      ...(d.calls || []).map((x: any) => ({ at: x.at, html: `<div style="padding:2px 0;"><span>${x.missed ? '☎️ <b style="color:var(--danger-text,#b91c1c);">Missed call</b>' : `☎️ ${this.esc(x.direction)} call`} · ${fmtAt(x.at)}${x.duration ? ` · ${Math.round(x.duration / 60)}m` : ''} <span class="rt-faint">(${this.esc(x.line || '')})</span></span></div>` })),
      ...(d.messages || []).map((x: any) => ({ at: x.at, html: `<div style="padding:2px 0;">💬 ${x.direction === 'incoming' ? '<b>Guest:</b>' : 'Us:'} ${this.esc(String(x.body || '').slice(0, 160))} <span class="rt-faint">· ${fmtAt(x.at)}</span></div>` })),
    ].sort((a, b) => String(b.at || '').localeCompare(String(a.at || ''))).slice(0, 25);
    return `<div style="padding:2px 14px;">${head}
      <div style="padding:4px 0 8px; font-size:12px; line-height:1.5; max-height:220px; overflow-y:auto;">${rows.map(r => r.html).join('') || '<span class="rt-faint">No calls or texts with this number yet.</span>'}</div>
    </div>`;
  }

  private async load(): Promise<void> {
    const [, tpl] = await Promise.all([
      // R114: the Day view never calls /board (its self-heal = a Hostaway fetch + an rt_checks rewrite)
      this.view === 'day' ? this.fetchDay() : this.fetchBoard(),
      this.get('/template'),
    ]);
    if (tpl && tpl.template) {
      this.template = tpl.template;
      if (!this.tplDirty) this.tplItems = this.cloneItems(this.template.items || []);
    }
    this.draw();
  }

  private async reloadBoard(): Promise<void> {
    await this.fetchBoard();
    this.draw();
  }
  // GET /board — sequenced (only the latest answer lands) and generation-
  // checked (lib viewFreshness). A failure is shown inline (error bar + Retry,
  // notes stay locked while the copy is stale) — never an alert over a
  // quietly editable stale copy.
  private async fetchBoard(): Promise<void> {
    const seq = ++this.boardSeq;
    const gen = this.fresh.begin('board');
    let ok = false;
    this.boardLoading = true;
    try {
      const r = await fetch(`${API}/board?days=${this.days}`);
      const j = await r.json().catch(() => ({}));
      if (seq !== this.boardSeq) return;
      if (r.ok && j && Array.isArray(j.checks)) { this.board = j; this.boardError = ''; ok = true; }
      else this.boardError = (j && j.error) || `request failed (${r.status})`;
    } catch (e: any) {
      if (seq === this.boardSeq) this.boardError = e?.message || 'network error';
    } finally {
      if (seq === this.boardSeq) { this.boardLoading = false; this.fresh.settle('board', gen, ok); }
    }
  }
  // R114 review: a write (tick / note / photo / ECI-LCO / generate / scan /
  // pre-arrival config) refreshes the view on screen; the other view's copy is
  // now stale and is refetched when the user switches to it.
  private markOtherViewStale(): void {
    this.fresh.invalidate(staleAfterWrite(this.view));
  }
  private refetchState() {
    return { hasBoard: !!this.board, boardStale: this.fresh.isStale('board'), hasDay: !!this.dayData, dayStale: this.fresh.isStale('day'), dayDate: this.dayData ? this.dayData.date : null, shownDay: this.shownDay() };
  }
  // R114 fix2: a saved note is patched into every cached copy at once (the
  // POST answers with the saved check), so the copy on screen is right even
  // while — or if — the follow-up reload fails; no note box has to lock for
  // an ordinary same-view reload.
  private patchNote(id: string, note: string): void {
    for (const c of ((this.board && this.board.checks) || [])) if (c && String(c.id) === id) c.note = note;
    for (const a of ((this.dayData && this.dayData.arrivals) || [])) if (a && a.check && String(a.check.id) === id) a.check.note = note;
  }

  // R114: every post-write reload goes through here — the Day view re-reads
  // /day (read-only), the board view /board.
  private async reloadActive(): Promise<void> {
    if (this.view !== 'day') return this.reloadBoard();
    await this.fetchDay();
    this.draw();
  }
  // GET /day — sequenced so a fast ‹ › run never paints an older day last.
  private async fetchDay(): Promise<void> {
    const seq = ++this.daySeq;
    const gen = this.fresh.begin('day');
    let ok = false;
    this.dayLoading = true;
    try {
      const r = await fetch(`${API}/day${this.day ? `?date=${encodeURIComponent(this.day)}` : ''}`);
      const j = await r.json().catch(() => ({}));
      if (seq !== this.daySeq) return;
      if (r.ok && j && Array.isArray(j.arrivals)) {
        this.dayData = j as DayPayload; this.dayError = ''; ok = true;
        // A stored day that IS the business today goes back to "follow today"
        // (so an overnight tab still rolls over) — decided on payload.today,
        // never on the device's date.
        if (this.day && collapseToToday(this.day, (j as DayPayload).today) === null) { this.day = null; this.writeStore(STORE_KEYS.day, null); }
      }
      else this.dayError = (j && j.error) || `request failed (${r.status})`;
    } catch (e: any) {
      if (seq === this.daySeq) this.dayError = e?.message || 'network error';
    } finally {
      if (seq === this.daySeq) { this.dayLoading = false; this.fresh.settle('day', gen, ok); }
    }
  }

  // ── fetch helpers (defensive — alert + null on failure) ─────
  private async get(path: string): Promise<any> {
    try {
      const r = await fetch(`${API}${path}`);
      const j = await r.json().catch(() => ({}));
      if (!r.ok) { alert(j.error || `request failed (${r.status})`); return null; }
      return j;
    } catch (e: any) { alert(e?.message || 'network error'); return null; }
  }
  private async post(path: string, body: any): Promise<any> {
    try {
      const r = await fetch(`${API}${path}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) { alert(j.error || `request failed (${r.status})`); return null; }
      return j;
    } catch (e: any) { alert(e?.message || 'network error'); return null; }
  }

  // ── formatting helpers ───────────────────────────────────────
  private esc(s: any): string { return Formatters.escapeHtml(s == null ? '' : String(s)); }
  // "Today" in the USER'S timezone (en-CA = YYYY-MM-DD). toISOString() is UTC,
  // which from ~5pm Pacific labels TOMORROW as today.
  private localToday(): string {
    try { return new Date().toLocaleDateString('en-CA'); } catch { return new Date().toISOString().split('T')[0]; }
  }
  private fmtDate(iso: string | null): string {
    if (!iso) return '—';
    const d = new Date(String(iso).slice(0, 10) + 'T00:00:00Z');
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' });
  }
  // "today" / "tomorrow" / "in Nd" for a YYYY-MM-DD check-in date.
  private relDay(isoDate: string | null): string {
    if (!isoDate) return '—';
    const today = new Date(this.localToday() + 'T00:00:00Z').getTime();
    const target = new Date(String(isoDate).slice(0, 10) + 'T00:00:00Z').getTime();
    if (isNaN(target)) return '—';
    const n = Math.round((target - today) / 86400000);
    if (n === 0) return 'today';
    if (n === 1) return 'tomorrow';
    return n > 0 ? `in ${n}d` : `${-n}d ago`;
  }
  private fmtH(h: number): string {
    const a = Math.abs(h);
    return a < 48 ? `${Math.round(a)}h` : `${Math.round(a / 24)}d`;
  }
  // Pending-item deadline: "due in 31h" / "overdue 5h" (caller styles red).
  private dueText(dueAt: string | null): { text: string; overdue: boolean } {
    if (!dueAt) return { text: '', overdue: false };
    const ms = Date.parse(dueAt) - Date.now();
    if (isNaN(ms)) return { text: '', overdue: false };
    const h = ms / 3600000;
    return h >= 0 ? { text: `due in ${this.fmtH(h)}`, overdue: false } : { text: `overdue ${this.fmtH(h)}`, overdue: true };
  }
  private fmtStamp(iso: string | null): string {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ' ' +
      d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  }
  private role(): string { try { return sessionStorage.getItem('userRole') || ''; } catch { return ''; } }
  // R105 #6 (now a Remote Ops hub tab → a fresh instance per hub render): veto
  // the 15-min timer while work is in flight so in-memory drafts (R48) survive.
  suspendAutoRefresh(): boolean {
    return !!(this.noteDrafts.size || this.noteSaving || this.uploadingItem || this.generating || this.tplDirty || (this.tplOpen && this.tplItems.length)
      || this.paDirty || this.paSaving || this.paScanBusy || this.paPolling);
  }
  private cloneItems(items: any[]): TplItem[] {
    return (items || []).map(it => ({
      key: String(it.key || ''),
      label: String(it.label || ''),
      description: it.description != null ? String(it.description) : '',
      required: it.required === 'optional' ? 'optional' : 'required',
      offset_hours: Number(it.offset_hours) || 0,
      channels: Array.isArray(it.channels) && it.channels.length ? it.channels.map((c: any) => String(c)) : null,
    }));
  }

  // ── actions ──────────────────────────────────────────────────
  private async generate(days = 30): Promise<void> {
    if (this.generating) return;
    this.generating = true; this.genNote = ''; this.draw();
    const r = await this.post(`/generate?days=${Math.max(1, Math.min(60, Math.round(days) || 30))}`, {});
    this.generating = false;
    if (r) this.genNote = `Synced: ${r.generated} new · ${r.refreshed} refreshed · ${r.total} upcoming arrivals tracked.`;
    this.fresh.invalidate('board'); this.fresh.invalidate('day');   // load() refreshes the active view; the other refetches on switch
    await this.load();
  }

  private async setItemState(id: string, key: string, state: string): Promise<void> {
    let note = '';
    if (state === 'waived') {
      const reason = prompt('Reason for waiving this item (required):');
      if (reason == null) return;
      note = reason.trim();
      if (!note) { alert('A reason is required to waive.'); return; }
    } else if (state === 'failed') {
      const n = prompt('What failed? (optional note)');
      if (n == null) return;
      note = n.trim();
    }
    const r = await this.post(`/checks/${encodeURIComponent(id)}/item`, { key, state, note });
    if (r) { this.markOtherViewStale(); await this.reloadActive(); }
  }

  private async saveTemplate(): Promise<void> {
    const items = this.tplItems.filter(it => (it.label || '').trim());
    if (!items.length) { alert('The template needs at least one item with a label.'); return; }
    const r = await this.post('/template', { items });
    if (r && r.template) {
      this.template = r.template;
      this.tplItems = this.cloneItems(r.template.items || []);
      this.tplDirty = false;
      this.tplNote = 'Saved — applies to newly generated arrivals; existing ones keep their checklist.';
      this.draw();
    }
  }

  // ── R114 pre-arrival auto-detect (Jackie item 5) ─────────────
  // The board carries c.prearrival {status, at, evidence, mode, can_tick} per
  // arrival and summary.prearrival_* (KPI + scanner status). Manual ticking is
  // never blocked by anything here.
  private relAgo(iso: string | null): string {
    if (!iso) return '';
    const ms = Date.now() - Date.parse(iso);
    if (!Number.isFinite(ms)) return '';
    const m = Math.round(ms / 60000);
    if (m < 1) return 'just now';
    if (m < 60) return `${m}m ago`;
    const h = Math.round(m / 60);
    return h < 48 ? `${h}h ago` : `${Math.round(h / 24)}d ago`;
  }
  private hostawayLink(c: any): string {
    return c && c.hostaway_id
      ? `<a class="rt-hwlink" href="https://dashboard.hostaway.com/reservations/${encodeURIComponent(String(c.hostaway_id))}" target="_blank" rel="noopener">View in Hostaway ↗</a>`
      : '';
  }
  // R114 review: the scanner status of the view on screen (the Day payload carries it too).
  private paScan(): any {
    return (this.view === 'day'
      ? (this.dayData && this.dayData.prearrival && this.dayData.prearrival.scan)
      : (this.board && this.board.summary && this.board.summary.prearrival_scan)) || null;
  }
  private paHint(c: any, it: any): string {
    const pa = c.prearrival;
    if (!pa || pa.item_key !== it.key || pa.mode === 'off') return '';
    const sc = this.paScan();
    if (pa.status === 'sent') {
      const when = pa.at ? ` ${this.esc(this.fmtStamp(pa.at))}` : '';
      const why = pa.mode === 'auto' && !pa.can_tick ? ' (keyword match, not auto-ticked)' : '';
      return `<span class="rt-pahint det" title="Hostaway shows an outgoing pre-arrival message in this reservation's thread. It proves the message was sent, not that the lock code or wifi were tested.">✉ Hostaway message detected${when}${why} — confirm with ✓ Done${this.hostawayLink(c)}</span>`;
    }
    if (pa.status === 'not_yet') return `<span class="rt-pahint">Hostaway checked ${this.esc(this.relAgo(pa.checked_at))} — not sent yet</span>`;
    if (pa.status === 'no_recent_activity') return `<span class="rt-pahint">No recent Hostaway activity — not sent yet${pa.checked_at ? ` · checked ${this.esc(this.relAgo(pa.checked_at))}` : ''}</span>`;
    if (pa.status === 'error') return `<span class="rt-pahint err">Couldn't read the Hostaway thread — tick by hand once it's sent</span>`;
    // R114 fix2: the scanner only looks at arrivals from today on — a past one never gets "Auto-check runs…"
    if (this.view === 'day' && c.check_in && c.check_in < this.dayToday()) return '';
    if (!sc || !sc.enabled) return '';
    const win = Number(sc.window_days) || 7;
    if (c.hours_to_checkin != null && c.hours_to_checkin > win * 24) return `<span class="rt-pahint">Auto-check starts ${win} days before arrival</span>`;
    return `<span class="rt-pahint">Auto-check runs every ${this.esc(sc.cadence_min || 30)} min</span>`;
  }
  private paBasis(s: any): string {
    const sc = s && s.prearrival_scan;
    if (!sc) return '';
    const mode = sc.mode === 'auto' ? 'Auto' : (sc.mode === 'shadow' ? 'Shadow (detect + confirm)' : 'Off');
    const r = this.role();
    const canScan = !r || TPL_EDIT_ROLES.includes(r);
    const checked = sc.mode === 'off' ? '' : (sc.last_ok_at ? ` · Hostaway checked ${this.esc(this.relAgo(sc.last_ok_at))}` : (sc.enabled ? ' · waiting for the first Hostaway check' : ''));
    const btn = sc.mode !== 'off' && sc.enabled && canScan
      ? `<button type="button" class="btn btn-ghost btn-sm rt-pa-scan" data-pa-scan ${this.paScanBusy ? 'disabled' : ''}>${this.paScanBusy ? 'Checking…' : '↻ Check Hostaway now'}</button>`
      : '';
    return ` <span class="rt-pa-basis">· pre-arrival auto-detect: ${mode}${checked}</span>${btn}${this.paScanNote ? ` <span class="rt-okmeta">${this.esc(this.paScanNote)}</span>` : ''}`;
  }
  private paBanner(s: any): string {
    const sc = s && s.prearrival_scan;
    if (!sc || sc.mode === 'off') return '';
    let why = '';
    if (!sc.enabled) why = sc.reason || 'the scanner is not running';
    else if (!sc.ok) why = sc.error || 'the last Hostaway check failed';
    else if (sc.stale) why = `no successful Hostaway check since ${sc.last_ok_at ? this.fmtStamp(sc.last_ok_at) : 'start-up'}`;
    if (!why) return '';
    return `<div class="rt-pa-banner" role="status"><span>⚠</span><span>Pre-arrival auto-detect paused (${this.esc(why)}) — tick “Pre-arrival info sent” by hand. Nothing else is blocked.</span></div>`;
  }
  private paKpi(s: any): string {
    if (!s || s.prearrival_pending == null) return '';
    const n = Number(s.prearrival_pending) || 0;
    // R114 fix2 (lib paTileText): a past day is history — never red, never "arrive in <48h"; untracked arrivals are named
    const t = paTileText({ pending: n, pending_48h: s.prearrival_pending_48h, detected: s.prearrival_detected, untracked: s.prearrival_untracked, past: !!s.prearrival_past, filterOn: this.paOnly });
    const tip = s.prearrival_past ? 'Arrivals on this past day whose “Pre-arrival info sent” item was never ticked — tap to show only them' : 'Arrivals whose “Pre-arrival info sent” item is still pending — tap to show only them';
    return `<button type="button" class="rt-kpi ${t.red ? 'red' : ''} ${this.paOnly ? 'on' : ''}" data-pa-kpi aria-pressed="${this.paOnly ? 'true' : 'false'}" title="${this.esc(tip)}">
      <div class="rt-kpi-v">${n}</div><div class="rt-kpi-l">${this.esc(t.label)}</div><div class="rt-kpi-s">${this.esc(t.sub)}</div></button>`;
  }
  private paFilterBar(n: number, o: { past?: boolean; untracked?: number | null } = {}): string {
    const un = Number(o.untracked) || 0;
    const what = o.past ? 'arrivals whose pre-arrival item was never ticked' : 'arrivals still waiting on their pre-arrival message';
    return `<div class="rt-pa-filter"><button type="button" class="chip active" data-pa-kpi aria-pressed="true">✉ ${o.past ? 'Pre-arrival not ticked' : 'Pre-arrival not sent'} <span class="chip-count">${n}</span> ✕</button><span>${what}${un ? ` · ${un} more without a checklist (status unknown)` : ''}</span></div>`;
  }
  private draftFromConfig(cfg: any): Record<string, any> {
    const j = (a: any) => (Array.isArray(a) ? a.join('\n') : '');
    const c = cfg || {};
    return {
      mode: c.mode || 'shadow',
      opening_phrases: j(c.opening_phrases), template_ids: j(c.template_ids),
      access_terms: j(c.access_terms), wifi_terms: j(c.wifi_terms), negative_phrases: j(c.negative_phrases),
      window_days_before: c.window_days_before ?? 7, window_end_hours_after_checkin: c.window_end_hours_after_checkin ?? 8,
      min_length: c.min_length ?? 150, scan_minutes: c.scan_minutes ?? 30,
      require_both_groups: !!c.require_both_groups, allow_split_messages: !!c.allow_split_messages,
      keywords_can_tick: !!c.keywords_can_tick, include_owner_stays: !!c.include_owner_stays, trust_sent_stamp: !!c.trust_sent_stamp,
    };
  }
  private async loadPaConfig(): Promise<void> {
    const j = await this.get('/prearrival');
    if (j && j.config) {
      this.paSaved = j;
      if (!this.paDirty) this.paDraft = this.draftFromConfig(j.config);
    }
    this.draw();
  }
  private async savePaConfig(): Promise<void> {
    if (!this.paDraft || this.paSaving) return;
    const d = this.paDraft;
    const lines = (v: any) => String(v || '').split(/\r?\n/).map(x => x.trim()).filter(Boolean);
    if (d.mode === 'auto' && !lines(d.opening_phrases).length && !lines(d.template_ids).length && !d.keywords_can_tick) {
      if (!confirm('Auto mode has no template phrase or id yet, so nothing can auto-tick until one is added (keyword matches only show “detected — confirm”). Save anyway?')) return;
    }
    const config = {
      mode: d.mode,
      opening_phrases: lines(d.opening_phrases), template_ids: lines(d.template_ids),
      access_terms: lines(d.access_terms), wifi_terms: lines(d.wifi_terms), negative_phrases: lines(d.negative_phrases),
      window_days_before: Number(d.window_days_before), window_end_hours_after_checkin: Number(d.window_end_hours_after_checkin),
      min_length: Number(d.min_length), scan_minutes: Number(d.scan_minutes),
      require_both_groups: !!d.require_both_groups, allow_split_messages: !!d.allow_split_messages,
      keywords_can_tick: !!d.keywords_can_tick, include_owner_stays: !!d.include_owner_stays, trust_sent_stamp: !!d.trust_sent_stamp,
    };
    this.paSaving = true; this.paNote = ''; this.draw();
    const r = await this.post('/prearrival/config', { config });
    this.paSaving = false;
    if (r && r.config) {
      this.paSaved = { ...(this.paSaved || {}), config: r.config, status: r.status || (this.paSaved && this.paSaved.status), saved: { at: r.config.updated_at, by: r.config.updated_by } };
      this.paDraft = this.draftFromConfig(r.config);
      this.paDirty = false;
      this.paNote = 'Saved — applies from the next Hostaway check.';
      this.markOtherViewStale();
      await this.reloadActive();   // R114 review: the Day view re-reads /day (never /board's self-heal)
      return;
    }
    this.draw();
  }
  private async scanNow(): Promise<void> {
    if (this.paScanBusy) return;
    this.paScanBusy = true; this.paScanNote = ''; this.draw();
    const r = await this.post('/prearrival/scan', {});
    this.paScanBusy = false;
    if (r) {
      const x = r.result || {};
      if (r.throttled) this.paScanNote = `Checked moments ago — try again in ${r.retry_in_s}s.`;
      else if (r.ok === false) this.paScanNote = `Couldn't check Hostaway: ${x.error || r.error || 'unknown error'}`;
      else this.paScanNote = `Checked ${x.candidates ?? 0} arrival${x.candidates === 1 ? '' : 's'} · ${x.found ?? 0} sent${x.ticked ? ` · ${x.ticked} ticked` : ''}.`;
    }
    this.markOtherViewStale();
    await this.reloadActive();   // R114 review: from the Day view this re-reads /day — never /board's generateCore
  }
  private async startPreview(): Promise<void> {
    if (this.paPolling) return;
    const j = await this.get('/prearrival/preview?days=14&back=30&refresh=1');
    if (!j) return;
    this.paPreview = j;
    this.draw();
    if (j.status === 'running') void this.pollPreview();
  }
  // Polls without a full redraw (the config inputs keep focus): only the
  // preview panel is patched until the job finishes.
  private async pollPreview(): Promise<void> {
    this.paPolling = true;
    try {
      for (let i = 0; i < 240; i++) {
        await new Promise(res => setTimeout(res, 2500));
        if (!document.body.contains(this.container)) return;
        const j = await fetch(`${API}/prearrival/preview?days=14&back=30`).then(r => (r.ok ? r.json() : null)).catch(() => null);
        if (!j) continue;
        this.paPreview = j;
        if (j.status !== 'running') break;
        this.patchPreview();
      }
    } finally { this.paPolling = false; }
    this.draw();
  }
  private patchPreview(): void {
    const el = document.getElementById('rt-pa-pv');
    if (!el) return;
    el.innerHTML = this.paPreviewInner();
    this.bindPaPreview();
  }
  private paPreviewInner(): string {
    const pv = this.paPreview;
    if (!pv) return '';
    if (pv.status === 'running') {
      const p = pv.progress;
      const step = p ? (p.phase === 'list' ? 'conversation list' : `messages ${p.done}/${p.total}`) : 'starting';
      return `<div class="rt-faint">Reading Hostaway… ${this.esc(step)} — read-only: nothing is sent or saved.</div>`;
    }
    if (pv.status === 'error') return `<div class="rt-ov">Preview failed: ${this.esc(pv.error || 'unknown error')}</div>`;
    const R = pv.result;
    if (!R) return '';
    const cov = R.coverage || {};
    const v = R.verdicts || {};
    const past = (R.agreement && R.agreement.past) || {};
    const up = (R.agreement && R.agreement.upcoming) || {};
    const pctTxt = (x: any) => (x == null ? '—' : `${x}%`);
    const stat = (val: any, label: string) => `<div class="rt-pa-stat"><b>${this.esc(val ?? '—')}</b><span>${label}</span></div>`;
    const detected = (Number(v.sent_template) || 0) + (Number(v.sent_keywords) || 0);
    const agRow = (label: string, g: any) => `<tr><td>${label}</td><td class="num">${g.detected_and_ticked ?? 0}</td><td class="num">${g.detected_not_ticked ?? 0}</td><td class="num">${g.ticked_not_detected ?? 0}</td><td class="num">${g.neither ?? 0}</td><td class="num">${pctTxt(g.agreement_pct)}</td></tr>`;
    const lg = R.lead_gap_hours || {}, sl = R.send_lead_hours || {}, sv = R.stamp_validation || {};
    // R114 review: "Use" only on HIGH-PRECISION candidates (lib/preArrivalPreview
    // use_ok — an id-like value seen on 3+ messages, ~once per thread, only on
    // Hostaway 'arrival' automations, 80%+ carrying the door/access wording).
    // Channel / type / event enums and one-offs show without a button.
    const pctOf = (v: any) => (v == null ? '—' : `${v}%`);
    const tk = (R.template_keys || []).slice(0, 8).map((k: any) => `<tr><td><code>${this.esc(k.key)}</code></td><td class="num">${k.messages}</td><td>${(k.values || []).slice(0, 6).map((x: any) => {
      const why = x.threads != null ? `${x.count} message${x.count === 1 ? '' : 's'} in ${x.threads} thread${x.threads === 1 ? '' : 's'} · arrival automation ${pctOf(x.arrival_pct)} · door/wifi wording ${pctOf(x.keyword_pct)}` : '';
      return `<span style="white-space:nowrap;" title="${this.esc(why)}">${this.esc(x.value)} <span class="rt-faint">×${x.count}${x.keyword_pct != null ? ` · ${this.esc(pctOf(x.keyword_pct))} door/wifi` : ''}</span>${x.use_ok === true ? ` <button type="button" class="btn btn-ghost btn-sm rt-pa-use" data-pa-usetid="${this.esc(`${k.key}=${x.value}`)}">Use</button>` : ''}</span>`;
    }).join(' · ')}</td></tr>`).join('');
    const cl = (R.opening_clusters || []).map((c: any) => `<tr><td>“${this.esc(c.phrase)}…”</td><td class="num">${c.count}</td><td class="num">${c.listings}</td><td class="num">${this.esc(pctOf(c.keyword_pct))}</td><td>${c.use_ok === true ? `<button type="button" class="btn btn-ghost btn-sm rt-pa-use" data-pa-usephrase="${this.esc(c.phrase)}">Use as opening phrase</button>` : '<span class="rt-faint" title="Fewer than 3 threads, a non-arrival automation, or under 80% carry the door / wifi wording">not precise enough</span>'}</td></tr>`).join('');
    const vp = (r: any) => {
      if (r.verdict === 'sent') return `<span class="rt-pa-v sent">sent · ${this.esc(r.evidence || '')}</span>`;
      if (r.verdict === 'not_sent') return `<span class="rt-pa-v miss">not found</span>`;
      return `<span class="rt-pa-v">${this.esc(String(r.verdict || '').replace(/_/g, ' '))}</span>`;
    };
    const rows = (R.rows || []).filter((r: any) => r.verdict === 'sent' || r.verdict === 'not_sent').slice(0, 80).map((r: any) => `<tr><td style="white-space:nowrap;">${this.esc(this.fmtDate(r.check_in))}</td><td>${this.esc(r.listing_name)}</td><td>${this.esc(r.channel || '')}</td><td>${vp(r)}</td><td class="num">${r.hours_before_checkin != null ? `${this.esc(r.hours_before_checkin)}h` : '—'}</td><td>${this.esc(r.manual || '')}${r.lead_gap_h != null ? ` <span class="rt-faint">(${r.lead_gap_h > 0 ? '+' : ''}${this.esc(r.lead_gap_h)}h)</span>` : ''}</td></tr>`).join('');
    return `
      <div class="rt-faint" style="margin-bottom:8px;">Preview of ${this.esc(R.params && R.params.from ? this.fmtDate(R.params.from) : '')} → ${this.esc(R.params && R.params.to ? this.fmtDate(R.params.to) : '')} · ${this.esc(this.fmtStamp(R.generated_at))} · read-only, nothing saved</div>
      <div class="rt-pa-stats">
        ${stat(cov.arrivals, 'arrivals')}
        ${stat(`${cov.in_list ?? 0} (${pctTxt(cov.found_pct)})`, 'in Hostaway’s recent list')}
        ${stat(cov.fetched, `threads read${cov.sampled ? ' (sampled)' : ''}`)}
        ${stat(detected, `detected · ${v.sent_template || 0} template`)}
        ${stat(pctTxt(past.agreement_pct), 'agree with team (past)')}
        ${stat(pctTxt(past.recall_pct), 'team ticks found (past)')}
      </div>
      <h4>Agreement with the team's manual ticks</h4>
      <div class="rt-pa-tablewrap"><table class="rt-pa-table"><thead><tr><th></th><th class="num">Detected &amp; ticked</th><th class="num">Detected, not ticked</th><th class="num">Ticked, not detected</th><th class="num">Neither</th><th class="num">Agree</th></tr></thead>
        <tbody>${agRow('Past arrivals', past)}${agRow('Upcoming', up)}</tbody></table></div>
      <div class="rt-pa-help">Team ticks land a median <b>${this.esc(lg.median ?? '—')}h</b> after the detected message (p10 ${this.esc(lg.p10 ?? '—')}h, p90 ${this.esc(lg.p90 ?? '—')}h). Messages go out a median <b>${this.esc(sl.median ?? '—')}h</b> before check-in. Hostaway's last-sent stamp matched the newest sent message in ${this.esc(sv.agree ?? 0)}/${this.esc(sv.compared ?? 0)} threads (${pctTxt(sv.agree_pct)}) — only tick “Skip threads whose last-sent stamp is unchanged” near 100%.</div>
      <h4>Opening phrases shared by 3+ homes</h4>
      ${cl ? `<div class="rt-pa-tablewrap"><table class="rt-pa-table"><thead><tr><th>Opening (digits dropped, guest → {name}, home → {home})</th><th class="num">Threads</th><th class="num">Homes</th><th class="num">Door/wifi</th><th></th></tr></thead><tbody>${cl}</tbody></table></div>` : '<div class="rt-faint">No opening shared by 3+ homes yet.</div>'}
      <h4>Template-like message fields</h4>
      <div class="rt-pa-help">“Use” appears only on values that identify one Hostaway arrival automation: 3+ messages, about one per guest thread, and 80%+ of them carrying the door / wifi wording. Hover a value for its numbers.</div>
      ${tk ? `<div class="rt-pa-tablewrap"><table class="rt-pa-table"><thead><tr><th>Field</th><th class="num">Messages</th><th>Values</th></tr></thead><tbody>${tk}</tbody></table></div>` : '<div class="rt-faint">Hostaway sent no template / automation / communication fields on these messages.</div>'}
      <h4>Arrivals read (first 80)</h4>
      ${rows ? `<div class="rt-pa-tablewrap"><table class="rt-pa-table"><thead><tr><th>Check-in</th><th>Home</th><th>Channel</th><th>Result</th><th class="num">Sent before check-in</th><th>Team</th></tr></thead><tbody>${rows}</tbody></table></div>` : '<div class="rt-faint">No threads read.</div>'}`;
  }
  private paAdminHtml(): string {
    const r = this.role();
    if (r && r !== 'admin' && r !== 'director') return '';
    const head = `<div class="rt-tpl-head" data-pa-toggle="1">
        <span class="rt-caret">${this.paOpen ? '▾' : '▸'}</span>
        <b>Pre-arrival auto-detect</b>
        <span class="rt-faint">admin · reads Hostaway's sent messages, never sends${this.paDirty ? ' · <span class="rt-warnmeta">unsaved changes</span>' : ''}</span>
      </div>`;
    if (!this.paOpen) return `<div class="rt-tpl rt-pa">${head}</div>`;
    if (!this.paDraft) return `<div class="rt-tpl rt-pa">${head}<div class="rt-tpl-body"><div class="rt-faint">Loading…</div></div></div>`;
    const d = this.paDraft;
    const sv = this.paSaved || {};
    const st = sv.status || {};
    const meta = sv.meta || {};
    const ta = (f: string, label: string, hint: string) => `<label class="rt-pa-field">${label}<textarea data-pa-f="${f}" rows="3" placeholder="${this.esc(hint)}">${this.esc(d[f] || '')}</textarea></label>`;
    const num = (f: string, label: string, min: number, max: number) => `<label class="rt-pa-field">${label}<input type="number" min="${min}" max="${max}" data-pa-f="${f}" value="${this.esc(d[f])}"></label>`;
    const tog = (f: string, label: string) => `<label><input type="checkbox" data-pa-f="${f}" ${d[f] ? 'checked' : ''}> ${label}</label>`;
    const modeHelp: Record<string, string> = {
      off: 'Off — no Hostaway reads; the item is ticked by hand as today.',
      shadow: 'Shadow — detects the sent message and shows “Hostaway message detected — confirm with ✓ Done” on the item. Never ticks.',
      auto: 'Auto — ticks “Pre-arrival info sent” with a ✉ chip once Hostaway shows the message went out (template evidence; keyword matches only if allowed below). ↩ Pending undoes it for good.',
    };
    const stamp = meta.stamp_total && meta.stamp_total.compared ? ` · last-sent stamp agreed ${meta.stamp_total.agree}/${meta.stamp_total.compared}` : '';
    const status = `Now: <b>${this.esc(st.mode || '—')}</b>${st.last_ok_at ? ` · last check ${this.esc(this.relAgo(st.last_ok_at))}` : ' · not checked yet'}${st.candidates != null ? ` · ${this.esc(st.candidates)} pending arrivals · ${this.esc(st.in_list ?? 0)} in Hostaway's recent list · ${this.esc(st.found ?? 0)} found` : ''}${stamp}${sv.saved && sv.saved.at ? ` · saved ${this.esc(this.fmtStamp(sv.saved.at))}${sv.saved.by ? ` by ${this.esc(sv.saved.by)}` : ''}` : ''}${st.reason ? ` · <span class="rt-warnmeta">${this.esc(st.reason)}</span>` : ''}`;
    const running = this.paPreview && this.paPreview.status === 'running';
    return `<div class="rt-tpl rt-pa">${head}
      <div class="rt-tpl-body">
        <div class="rt-pa-status">${status}</div>
        <div class="seg-tabs" role="group" aria-label="Auto-detect mode">${[['off', 'Off'], ['shadow', 'Shadow'], ['auto', 'Auto']].map(([m, l]) =>
          `<button type="button" class="seg-tab ${d.mode === m ? 'active' : ''}" data-pa-mode="${m}" aria-pressed="${d.mode === m ? 'true' : 'false'}">${l}</button>`).join('')}</div>
        <div class="rt-pa-help">${this.esc(modeHelp[d.mode] || '')} An auto tick proves the message was <b>sent</b> — not that the lock code was programmed or the wifi tested.</div>
        <div class="rt-pa-grid">
          ${ta('opening_phrases', 'Template opening phrase(s)', "one per line — e.g. hi {name} we're excited to host you")}
          ${ta('template_ids', 'Template / automation ids', 'key=value per line — pick one from the preview')}
          ${ta('access_terms', 'Door / access words', 'one per line')}
          ${ta('wifi_terms', 'Wifi words', 'one per line')}
          ${ta('negative_phrases', '“Not yet” phrases', 'one per line — e.g. will be sent')}
        </div>
        <div class="rt-pa-nums">
          ${num('window_days_before', 'Days before check-in', 1, 21)}
          ${num('window_end_hours_after_checkin', 'Hours after check-in', 0, 24)}
          ${num('min_length', 'Min message length', 0, 2000)}
          ${num('scan_minutes', 'Check every (min)', 10, 240)}
        </div>
        <div class="rt-pa-toggles">
          ${tog('require_both_groups', 'Require door AND wifi wording')}
          ${tog('allow_split_messages', 'Allow door + wifi in two messages')}
          ${tog('keywords_can_tick', 'Keyword matches can auto-tick')}
          ${tog('include_owner_stays', 'Include owner stays')}
          ${tog('trust_sent_stamp', 'Skip threads whose last-sent stamp is unchanged')}
        </div>
        <div class="rt-pa-foot">
          <button type="button" class="btn btn-primary btn-sm" data-pa-save ${this.paSaving ? 'disabled' : ''}>${this.paSaving ? 'Saving…' : 'Save'}</button>
          <button type="button" class="btn btn-secondary btn-sm" data-pa-preview ${running ? 'disabled' : ''}>${running ? 'Previewing…' : 'Preview last 30 days'}</button>
          ${this.paDirty ? '<button type="button" class="btn btn-ghost btn-sm" data-pa-reset>Discard changes</button>' : ''}
          ${this.paNote ? `<span class="rt-okmeta">${this.esc(this.paNote)}</span>` : ''}
        </div>
        <div class="rt-pa-pv" id="rt-pa-pv" ${this.paPreview ? '' : 'hidden'}>${this.paPreviewInner()}</div>
      </div>
    </div>`;
  }
  private bindPa(): void {
    const all = <T extends HTMLElement>(sel: string) => this.container.querySelectorAll<T>(sel);
    all('[data-pa-kpi]').forEach(el => el.addEventListener('click', () => { this.paOnly = !this.paOnly; this.draw(); }));
    all('[data-pa-scan]').forEach(el => el.addEventListener('click', (ev) => { ev.stopPropagation(); void this.scanNow(); }));
    all('.rt-hwlink').forEach(el => el.addEventListener('click', ev => ev.stopPropagation()));
    all('[data-pa-toggle]').forEach(el => el.addEventListener('click', () => {
      this.paOpen = !this.paOpen;
      if (this.paOpen && !this.paDraft) void this.loadPaConfig();
      this.draw();
    }));
    all('[data-pa-mode]').forEach(el => el.addEventListener('click', () => {
      if (!this.paDraft) return;
      this.paDraft.mode = el.dataset.paMode || 'shadow';
      this.paDirty = true; this.paNote = ''; this.draw();
    }));
    // STATE-BACKED inputs: mirror every keystroke / toggle into the draft (no redraw — keeps focus)
    all<HTMLInputElement>('[data-pa-f]').forEach(el => {
      const f = el.dataset.paF || '';
      const isBox = el.type === 'checkbox';
      el.addEventListener(isBox ? 'change' : 'input', () => {
        if (!this.paDraft || !f) return;
        this.paDraft[f] = isBox ? el.checked : (el.type === 'number' ? Number(el.value) : el.value);
        this.paDirty = true;
      });
    });
    all('[data-pa-save]').forEach(el => el.addEventListener('click', () => { void this.savePaConfig(); }));
    all('[data-pa-reset]').forEach(el => el.addEventListener('click', () => {
      this.paDraft = this.draftFromConfig(this.paSaved && this.paSaved.config);
      this.paDirty = false; this.paNote = ''; this.draw();
    }));
    all('[data-pa-preview]').forEach(el => el.addEventListener('click', () => { void this.startPreview(); }));
    this.bindPaPreview();
  }
  private bindPaPreview(): void {
    const add = (field: string, value: string) => {
      if (!this.paDraft || !value) return;
      const cur = String(this.paDraft[field] || '').split(/\r?\n/).map(x => x.trim()).filter(Boolean);
      if (!cur.includes(value)) cur.push(value);
      this.paDraft[field] = cur.join('\n');
      this.paDirty = true; this.paNote = 'Added — review, then Save.'; this.draw();
    };
    this.container.querySelectorAll<HTMLElement>('[data-pa-usephrase]').forEach(el => el.addEventListener('click', () => add('opening_phrases', el.dataset.paUsephrase || '')));
    this.container.querySelectorAll<HTMLElement>('[data-pa-usetid]').forEach(el => el.addEventListener('click', () => add('template_ids', el.dataset.paUsetid || '')));
  }

  // ── draw ─────────────────────────────────────────────────────
  private draw(): void {
    const body = document.getElementById('rt-body');
    if (!body) return;
    const dayView = this.view === 'day';   // R114
    if (!dayView && !this.board) {
      body.innerHTML = this.boardLoading
        ? `<div style="padding:40px; text-align:center;">${LoadingSpinner.render()}</div>`
        : emptyState('Arrivals board unavailable', `GET ${API}/board failed${this.boardError ? ` (${this.esc(this.boardError)})` : ''} — check the server log, then reload.`, '⚠️');
      return;
    }
    const s = (this.board && this.board.summary) || {};
    const checks: any[] = (this.board && this.board.checks) || [];
    const escalations = checks.filter(c => c.rollup === 'at_risk' || c.rollup === 'blocked');
    // R114: "Pre-arrival not sent" filter — only arrivals whose pre-arrival item is still pending
    const paRows = this.paOnly ? checks.filter(c => c.prearrival && c.prearrival.state === 'pending') : checks;

    const kpi = (v: any, label: string, sub: string, tone = '') => `
      <div class="rt-kpi ${tone}"><div class="rt-kpi-v">${v ?? 0}</div><div class="rt-kpi-l">${label}</div><div class="rt-kpi-s">${sub}</div></div>`;

    const pill = (rollup: string) => {
      const [bg, fg, label] = PILL[rollup] || PILL.pending;
      return `<span class="rt-pill" style="background:${bg}; color:${fg};${rollup === 'blocked' ? ' border:1px solid var(--danger, #dc2626);' : ''}">${label}</span>`;
    };

    // R16 #1: escalations render as a compact LIST (color-coded rows), not
    // blocks — the strip ate half the screen once a few arrivals were at risk.
    const escCard = (c: any) => {
      const outstanding = (c.items || []).filter((it: any) =>
        it.required === 'required' && (it.state === 'pending' || it.state === 'failed'));
      const when = c.hours_to_checkin != null && c.hours_to_checkin >= 0 ? `in ${this.fmtH(c.hours_to_checkin)}` : this.relDay(c.check_in);
      const open = this.expanded.has(String(c.id));
      // Expands INLINE right here (R18 #11) — no jump down to the board row.
      return `
        <div class="rt-esc-item ${c.rollup === 'blocked' ? 'blocked' : 'atrisk'} ${open ? 'open' : ''}">
          <div class="rt-esc-row" data-toggle="${this.esc(c.id)}">
            <span class="rt-caret" style="flex:0 0 auto;">${open ? '▾' : '▸'}</span>
            <b style="flex:0 0 auto;">${this.esc(c.guest_name || '(no guest name)')}</b>
            <span class="rt-esc-sub">${this.esc(c.listing_name)}</span>
            <span class="rt-esc-sub" style="white-space:nowrap;">arrives ${when}</span>
            <span class="rt-esc-inline">${outstanding.map((it: any) =>
              `<span class="rt-esc-chip ${it.state === 'failed' ? 'failed' : ''}">${this.esc(it.label)}</span>`).join('') || '<span class="rt-esc-chip">—</span>'}</span>
            ${pill(c.rollup)}
          </div>
          ${open ? detailBody(c) : ''}
        </div>`;
    };

    const itemActs = (c: any, it: any) => {
      const btn = (state: string, label: string, cls = '') =>
        `<button class="rt-ibtn ${cls}" data-istate="${state}" data-id="${this.esc(c.id)}" data-key="${this.esc(it.key)}">${label}</button>`;
      if (it.state === 'pending') {
        return btn('done', '✓ Done', 'ok') + btn('waived', 'Waive') + btn('na', 'N/A') + btn('failed', 'Failed', 'bad');
      }
      return btn('pending', '↩ Pending');
    };

    const itemMeta = (c: any, it: any) => {
      if (it.state === 'pending') {
        const d = this.dueText(it.due_at);
        const due = d.text ? `<span class="${d.overdue ? 'rt-ov' : 'rt-due'}">${d.text}</span>` : '';
        return due + this.paHint(c, it);   // R114: pre-arrival auto-detect status line
      }
      if (it.state === 'na') return `<span class="rt-faint">not applicable${c.channel ? ` on ${this.esc(c.channel)}` : ''}</span>`;
      // R48: machine ticks show as a chip, not a person name. R114: every
      // 'auto:' source is a chip; the Hostaway pre-arrival MESSAGE gets its own
      // ✉ chip (info tone) so it never reads like a payment fact or a person.
      const by = String(it.done_by || '');
      const isAuto = by.startsWith('auto:');
      if (by === 'auto:hostaway_msg' && it.state === 'done') {
        const at = (it.auto && it.auto.at) || it.done_at;
        const how = it.auto && it.auto.evidence === 'template' ? 'matched the pre-arrival template' : 'matched door-code + wifi wording';
        const tip = `Hostaway shows the pre-arrival message was sent${at ? ` at ${this.fmtStamp(at)}` : ''} (${how}). This proves the message went out — not that the lock code or wifi were tested. ↩ Pending to undo; it won't re-tick.`;
        return `<span class="rt-autochip msg" title="${this.esc(tip)}">✉ auto · Hostaway message${at ? ` · ${this.esc(this.fmtStamp(at))}` : ''}</span>${this.hostawayLink(c)}`;
      }
      const who = isAuto ? `<span class="rt-autochip">✓ auto (${by.startsWith('auto:hostaway') ? 'Hostaway' : this.esc(by.slice(5) || 'system')})</span>` : (it.done_by ? this.esc(it.done_by) : '—');
      const when = this.fmtStamp(it.done_at);
      const stamp = `${who}${when ? ` · ${when}` : ''}`;
      if (it.state === 'done') return `<span class="rt-okmeta">${isAuto ? '' : '✓ '}${stamp}</span>`;
      if (it.state === 'waived') return `<span class="rt-warnmeta">waived by ${stamp}${it.note ? ` — ${this.esc(it.note)}` : ''}</span>`;
      return `<span class="rt-ov">failed · ${stamp}${it.note ? ` — ${this.esc(it.note)}` : ''}</span>`;
    };

    const itemRow = (c: any, it: any) => {
      const photos = Array.isArray(it.photos) ? it.photos : [];
      const busy = this.uploadingItem === `${c.id}|${it.key}`;
      const thumbs = photos.map((u: string) => `<a href="${this.esc(u)}" target="_blank" rel="noopener"><img src="${this.esc(u)}" style="width:40px; height:40px; object-fit:cover; border-radius:6px; border:1px solid var(--border); margin:2px 2px 0 0;"></a>`).join('');
      return `
      <div class="rt-item st-${this.esc(it.state)} ${it.overdue ? 'overdue' : ''}">
        <span class="rt-idot"></span>
        <div class="rt-imain">
          <div class="rt-ilabel">${this.esc(it.label)}${it.required === 'optional' ? ' <span class="rt-faint">(optional)</span>' : ''}</div>
          <div class="rt-imeta">${itemMeta(c, it)}</div>
          ${thumbs ? `<div style="display:flex; flex-wrap:wrap; margin-top:4px;">${thumbs}</div>` : ''}
        </div>
        <div class="rt-iacts">${itemActs(c, it)}
          <label class="rt-ibtn" title="Attach a verification screenshot" style="cursor:pointer;">${busy ? '…' : '📷'}<input type="file" accept="image/*" style="display:none;" data-itemphoto="${this.esc(c.id)}|${this.esc(it.key)}" ${busy ? 'disabled' : ''}></label>
        </div>
      </div>`;
    };

    // R48 check-level note — value comes from the draft when one exists (draft
    // survives redraws; textarea DOM does not).
    const noteBlock = (c: any) => {
      const id = String(c.id);
      const val = this.noteDrafts.has(id) ? (this.noteDrafts.get(id) as string) : String(c.note || '');
      // R114 review → fix2 (lib noteLock): locked while this view's copy is
      // STALE (written in the other view; refetching, or the refetch failed) —
      // a stale copy must never seed a note save. An ordinary reload after a
      // write in this view does not lock (the saved note is patched in).
      const day = this.view === 'day';
      const lock = noteLock({ saving: this.noteSaving === id, stale: this.fresh.isStale(day ? 'day' : 'board'), loading: day ? this.dayLoading : this.boardLoading });
      const tip = lock.why ? ` title="${this.esc(lock.why)}"` : '';
      return `<div class="rt-notewrap">
          <textarea class="rt-notein" rows="2" maxlength="2000" placeholder="Reservation note — payment plan, ID follow-up, anything the team should know…" data-noteid="${this.esc(id)}" ${lock.disabled ? 'disabled' : ''}${tip}>${this.esc(val)}</textarea>
          <button class="rt-btn" data-notesave="${this.esc(id)}" ${lock.disabled ? 'disabled' : ''}${tip}>${this.esc(lock.label)}</button>
        </div>`;
    };

    // The reservation detail body — shared by the board row AND the escalation
    // card so clicking either expands the SAME facts inline (R18 #11).
    const detailBody = (c: any) => `<div class="rt-row-body">
        <div class="rt-facts">
          ${c.check_in ? `<span>📅 ${this.esc(this.fmtDate(c.check_in))} → ${this.esc(this.fmtDate(c.check_out))}${c.nights ? ` · ${this.esc(c.nights)} night${c.nights === 1 ? '' : 's'}` : ''}</span>` : ''}
          ${c.number_of_guests != null ? `<span>👥 ${this.esc(c.number_of_guests)} guest${c.number_of_guests === 1 ? '' : 's'}${(c.adults != null || c.children != null) ? ` (${[c.adults != null ? `${this.esc(c.adults)} adult${c.adults === 1 ? '' : 's'}` : '', c.children ? `${this.esc(c.children)} child${c.children === 1 ? '' : 'ren'}` : '', c.infants ? `${this.esc(c.infants)} infant${c.infants === 1 ? '' : 's'}` : ''].filter(Boolean).join(', ')})` : ''}</span>` : ''}
          ${c.pets ? `<span>🐾 ${this.esc(c.pets)} pet${c.pets === 1 ? '' : 's'}</span>` : ''}
          ${c.guest_phone ? `<span>📞 <a href="tel:${this.esc(c.guest_phone)}">${this.esc(c.guest_phone)}</a></span>` : ''}
          ${c.confirmation_code ? `<span class="rt-faint">#${this.esc(c.confirmation_code)}</span>` : ''}
        </div>
        ${noteBlock(c)}
        ${this.quoBlock(c)}
        ${(c.items || []).map((it: any) => itemRow(c, it)).join('') || '<div class="rt-faint" style="padding:8px 14px;">No checklist items.</div>'}</div>`;

    const row = (c: any) => {
      const open = this.expanded.has(String(c.id));
      return `
        <div class="rt-row" id="rt-row-${this.esc(c.id)}">
          <div class="rt-row-head" data-toggle="${this.esc(c.id)}">
            <span class="rt-caret">${open ? '▾' : '▸'}</span>
            <b class="rt-guest">${this.esc(c.guest_name || '(no guest name)')}</b>
            <span class="rt-prop">${this.esc(c.listing_name)}</span>
            <span class="rt-when">${this.fmtDate(c.check_in)} <span class="rt-rel">${this.relDay(c.check_in)}</span></span>
            <span class="rt-chan">${this.esc(c.channel || '—')}</span>${c.reservation_status === 'ownerStay' ? '<span class="rt-chan" style="background:#faf5ff; color:#7c3aed;">👤 Owner stay</span>' : ''}
            ${c.payment_flag ? `<span class="rt-chan" title="${c.payment_flag === 'unpaid_24h' ? 'Booked 24h+ ago with no payment collected — see the Issues board' : '2nd payment due — inside 2 weeks of check-in and not fully paid'}" style="background:${c.payment_severity === 'emergency' ? '#fee2e2' : '#fef3c7'}; color:${c.payment_severity === 'emergency' ? '#b91c1c' : '#92400e'}; font-weight:700;">💳 ${c.payment_flag === 'unpaid_24h' ? 'UNPAID 24h+' : '2nd payment due'}</span>` : ''}
            <button class="rt-chan" data-rt-ecilco="${this.esc(String(c.listing_id))}|${this.esc(c.check_in)}|eci|${c.eci ? 1 : 0}" title="Early check-in — tap to toggle; feeds the HK + inspection tasks" style="cursor:pointer; border:1px solid ${c.eci ? '#b45309' : 'var(--border,#e2e8f0)'}; ${c.eci ? 'background:#fffbeb; color:#b45309; font-weight:700;' : 'opacity:.65;'}">⏰ ECI</button>
            <button class="rt-chan" data-rt-ecilco="${this.esc(String(c.listing_id))}|${this.esc(c.check_out || '')}|lco|${c.lco ? 1 : 0}" ${c.check_out ? '' : 'disabled'} title="${c.check_out ? 'Late check-out — tap to toggle; feeds the HK + inspection tasks' : 'No check-out date on file'}" style="cursor:${c.check_out ? 'pointer' : 'default'}; border:1px solid ${c.lco ? '#1d4ed8' : 'var(--border,#e2e8f0)'}; ${c.lco ? 'background:#eff6ff; color:#1d4ed8; font-weight:700;' : 'opacity:.65;'}">🌙 LCO</button>
            ${c.note ? `<span class="rt-notechip" title="${this.esc(c.note)}">📝 ${this.esc(String(c.note).slice(0, 60))}${String(c.note).length > 60 ? '…' : ''}</span>` : ''}
            <span class="rt-prog">${c.required_done ?? 0}/${c.required_total ?? 0} required</span>
            ${pill(c.rollup)}
          </div>
          ${open ? detailBody(c) : ''}
        </div>`;
    };

    // Template editor (office only — role from sessionStorage, set by /api/me).
    const r = this.role();
    const canEditTpl = !r || TPL_EDIT_ROLES.includes(r);
    const tplRow = (it: TplItem, i: number) => `
      <div class="rt-tpl-row">
        <input class="rt-tin lbl" data-tf="label" data-ti="${i}" value="${this.esc(it.label)}" placeholder="Item label">
        <label class="rt-toff">due <input class="rt-tin off" type="number" min="0" max="720" data-tf="offset_hours" data-ti="${i}" value="${this.esc(it.offset_hours)}">h before check-in</label>
        <button class="rt-treq ${it.required === 'required' ? 'on' : ''}" data-treq="1" data-ti="${i}">${it.required === 'required' ? 'Required' : 'Optional'}</button>
        <span class="rt-tchs">${CHANNELS.map(ch =>
          `<button class="rt-tch ${it.channels && it.channels.includes(ch) ? 'on' : ''}" data-tch="${ch}" data-ti="${i}">${ch}</button>`).join('')}</span>
        <button class="rt-tdel" data-tdel="1" data-ti="${i}" title="Remove item">✕</button>
      </div>`;
    const tplHtml = !canEditTpl ? '' : `
      <div class="rt-tpl">
        <div class="rt-tpl-head" data-tpltoggle="1">
          <span class="rt-caret">${this.tplOpen ? '▾' : '▸'}</span>
          <b>Checklist template</b>
          <span class="rt-faint">applies to new arrivals AND any arrival the team hasn't started yet (on next refresh) · in-progress checklists are never changed${this.tplDirty ? ' · <span class="rt-warnmeta">unsaved changes</span>' : ''}</span>
        </div>
        ${!this.tplOpen ? '' : `
          <div class="rt-tpl-body">
            <div class="rt-faint" style="margin-bottom:8px;">Channel flags: none selected = applies on ALL channels; otherwise the item is auto-N/A off those channels.</div>
            ${this.tplItems.map((it, i) => tplRow(it, i)).join('')}
            <div class="rt-tpl-foot">
              <button class="rt-btn" data-tadd="1">+ Add item</button>
              <button class="rt-btn primary" data-tsave="1">Save template</button>
              ${this.tplNote ? `<span class="rt-okmeta">${this.esc(this.tplNote)}</span>` : ''}
            </div>
          </div>`}
      </div>`;

    body.innerHTML = `
      <style>
        .rt-head { display:flex; align-items:center; gap:12px; flex-wrap:wrap; margin-bottom:4px; }
        .rt-head h2 { margin:0; font-size:16px; font-weight:600; }
        .rt-head .sub { font-size:12px; color:var(--text-muted); }
        .rt-note { font-size:11px; color:var(--success-text, #166534); background:var(--success-soft); border:1px solid var(--success, #16a34a); border-radius:6px; padding:6px 11px; margin:8px 0 2px; }
        .rt-basis { font-size:11px; color:var(--text-faint, #9ca3af); margin:4px 0 12px; }
        .rt-kpis { display:flex; gap:10px; flex-wrap:wrap; margin-bottom:14px; }
        .rt-kpi { min-width:110px; padding:10px 14px; border-radius:var(--radius); border:1px solid var(--border); background:#fff; }
        .rt-kpi.red { background:var(--danger-soft); border-color:var(--danger, #dc2626); }
        .rt-kpi.red .rt-kpi-v { color:var(--danger-text); }
        .rt-kpi-v { font-size:22px; font-weight:600; font-variant-numeric:tabular-nums; }
        .rt-kpi-l { font-size:10px; text-transform:uppercase; letter-spacing:.05em; opacity:.8; }
        .rt-kpi-s { font-size:10px; color:var(--text-faint, #9ca3af); margin-top:2px; }
        .rt-esc-strip { display:flex; flex-direction:column; gap:5px; margin-bottom:14px; }
        .rt-esc-item { border-radius:8px; overflow:hidden; }
        .rt-esc-item.atrisk { background:var(--warning-soft); border-left:3px solid var(--warning, #f59e0b); }
        .rt-esc-item.blocked { background:var(--danger-soft); border-left:3px solid var(--danger, #dc2626); }
        .rt-esc-item.open { box-shadow:0 1px 3px rgba(0,0,0,.12); }
        .rt-esc-row { display:flex; gap:10px; align-items:center; padding:7px 12px; cursor:pointer; font-size:12.5px; overflow:hidden; }
        .rt-esc-row:hover { filter:brightness(0.97); }
        .rt-esc-item .rt-row-body { background:var(--card, #fff); }
        .rt-esc-sub { color:var(--text-muted); font-size:12px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
        .rt-esc-inline { display:flex; gap:4px; flex-wrap:wrap; margin-left:auto; }
        .rt-esc-top { display:flex; align-items:center; justify-content:space-between; gap:8px; font-size:13px; }
        .rt-esc-prop { font-size:11.5px; color:var(--text-muted); margin-top:2px; }
        .rt-esc-when { font-size:11px; font-weight:700; color:var(--danger-text); margin-top:2px; }
        .rt-esc-items { display:flex; gap:4px; flex-wrap:wrap; margin-top:6px; }
        .rt-esc-chip { font-size:10px; font-weight:600; padding:2px 7px; border-radius:999px; background:#fff; border:1px solid var(--border); color:var(--text); }
        .rt-esc-chip.failed { border-color:var(--danger, #dc2626); color:var(--danger-text); }
        .rt-allclear { font-size:12px; color:var(--success-text, #166534); background:var(--success-soft); border-radius:8px; padding:8px 12px; margin-bottom:14px; }
        .rt-board { display:flex; flex-direction:column; gap:8px; }
        .rt-row { border:1px solid var(--border); border-radius:10px; background:var(--card, #fff); overflow:hidden; }
        .rt-row-head { display:flex; align-items:center; gap:10px; padding:10px 14px; cursor:pointer; font-size:12.5px; user-select:none; flex-wrap:wrap; }
        .rt-row-head:hover { background:var(--bg-subtle); }
        .rt-caret { color:var(--text-muted); font-size:11px; width:12px; }
        .rt-guest { font-size:13px; }
        .rt-prop { color:var(--text-muted); }
        .rt-when { color:var(--text); white-space:nowrap; }
        .rt-rel { color:var(--text-faint, #9ca3af); font-size:11px; }
        .rt-chan { font-size:10px; font-weight:600; padding:2px 8px; border-radius:999px; background:var(--bg-subtle); color:var(--text-muted); text-transform:capitalize; }
        .rt-prog { margin-left:auto; font-size:11px; color:var(--text-muted); font-variant-numeric:tabular-nums; white-space:nowrap; }
        .rt-pill { display:inline-block; font-size:10px; font-weight:700; padding:2px 9px; border-radius:999px; white-space:nowrap; }
        .rt-row-body { border-top:1px solid var(--border); }
        .rt-facts { display:flex; gap:16px; flex-wrap:wrap; padding:9px 14px; font-size:12px; color:var(--text-muted); border-bottom:1px solid var(--border-soft, #f1f5f9); background:var(--bg-subtle); }
        .rt-notewrap { display:flex; gap:8px; align-items:flex-start; padding:8px 14px; border-bottom:1px solid var(--border-soft, #f1f5f9); background:var(--bg-subtle); }
        .rt-notein { flex:1; min-width:0; padding:6px 9px; border:1px solid var(--border); border-radius:6px; font-size:12px; font-family:inherit; resize:vertical; }
        .rt-notechip { font-size:10.5px; color:var(--text-muted); background:var(--bg-subtle); border:1px solid var(--border-soft, #f1f5f9); border-radius:999px; padding:2px 8px; max-width:220px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
        .rt-autochip { display:inline-block; font-size:9.5px; font-weight:700; padding:1px 7px; border-radius:999px; background:var(--success-soft); color:var(--success-text, #166534); border:1px solid var(--success, #16a34a); }
        .rt-facts a { color:var(--info, #1d4ed8); text-decoration:none; }
        .rt-item { display:flex; align-items:center; gap:10px; padding:8px 14px; border-bottom:1px solid var(--border-soft, #f1f5f9); }
        .rt-item:last-child { border-bottom:none; }
        .rt-item.overdue { background:var(--danger-soft); }
        .rt-idot { width:8px; height:8px; border-radius:50%; background:var(--border); flex:none; }
        .rt-item.st-done .rt-idot { background:var(--success, #16a34a); }
        .rt-item.st-waived .rt-idot { background:var(--info, #1d4ed8); }
        .rt-item.st-failed .rt-idot { background:var(--danger, #dc2626); }
        .rt-item.st-na .rt-idot { background:var(--border); opacity:.4; }
        .rt-item.st-na .rt-ilabel { color:var(--text-faint, #9ca3af); text-decoration:line-through; }
        .rt-imain { flex:1; min-width:0; }
        .rt-ilabel { font-size:12.5px; font-weight:500; }
        .rt-imeta { font-size:11px; margin-top:1px; }
        .rt-due { color:var(--text-muted); }
        .rt-ov { color:var(--danger-text); font-weight:700; }
        .rt-okmeta { color:var(--success-text, #166534); }
        .rt-warnmeta { color:var(--warning-text); }
        .rt-faint { color:var(--text-faint, #9ca3af); }
        .rt-iacts { display:flex; gap:5px; flex-wrap:wrap; }
        .rt-ibtn { font-size:10px; font-weight:600; padding:3px 9px; border:1px solid var(--border); border-radius:6px; background:#fff; color:var(--text-muted); cursor:pointer; white-space:nowrap; }
        .rt-ibtn:hover { border-color:#9ca3af; color:var(--text); }
        .rt-ibtn.ok { border-color:var(--success, #16a34a); color:var(--success-text, #166534); }
        .rt-ibtn.bad { border-color:var(--danger, #dc2626); color:var(--danger-text); }
        .rt-empty { border:1px dashed var(--border); border-radius:10px; padding:24px; text-align:center; color:var(--text-muted); font-size:13px; }
        .rt-tpl { border:1px solid var(--border); border-radius:10px; background:var(--card, #fff); margin-top:16px; overflow:hidden; }
        .rt-tpl-head { display:flex; align-items:center; gap:10px; padding:11px 14px; cursor:pointer; font-size:13px; user-select:none; }
        .rt-tpl-head:hover { background:var(--bg-subtle); }
        .rt-tpl-head .rt-faint { font-size:11px; }
        .rt-tpl-body { border-top:1px solid var(--border); padding:12px 14px; }
        .rt-tpl-row { display:flex; align-items:center; gap:8px; flex-wrap:wrap; padding:6px 0; border-bottom:1px solid var(--border-soft, #f1f5f9); }
        .rt-tin { padding:5px 8px; border:1px solid var(--border); border-radius:6px; font-size:12px; }
        .rt-tin.lbl { flex:1; min-width:200px; }
        .rt-tin.off { width:58px; text-align:right; }
        .rt-toff { font-size:11px; color:var(--text-muted); white-space:nowrap; display:flex; align-items:center; gap:4px; }
        .rt-treq { font-size:10px; font-weight:700; padding:3px 10px; border-radius:999px; border:1px solid var(--border); background:#fff; color:var(--text-muted); cursor:pointer; }
        .rt-treq.on { background:var(--danger-soft); color:var(--danger-text); border-color:var(--danger, #dc2626); }
        .rt-tchs { display:flex; gap:3px; flex-wrap:wrap; }
        .rt-tch { font-size:9.5px; font-weight:600; padding:2px 7px; border-radius:999px; border:1px solid var(--border); background:#fff; color:var(--text-faint, #9ca3af); cursor:pointer; }
        .rt-tch.on { background:var(--text); color:#fff; border-color:var(--text); }
        .rt-tdel { border:none; background:none; color:var(--text-faint, #9ca3af); cursor:pointer; font-size:12px; }
        .rt-tdel:hover { color:var(--danger-text); }
        .rt-tpl-foot { display:flex; align-items:center; gap:8px; margin-top:10px; font-size:11px; }
        .rt-btn { font-size:11px; font-weight:600; padding:5px 12px; border:1px solid var(--border); border-radius:7px; background:#fff; color:var(--text); cursor:pointer; }
        .rt-btn.primary { background:var(--accent); border-color:var(--accent); color:#fff; }
        /* R114 pre-arrival auto-detect */
        .rt-autochip.msg { background:var(--info-soft); color:var(--info-text); border-color:var(--accent-border); font-weight:600; }
        .rt-hwlink { font-size:10.5px; color:var(--accent); text-decoration:none; margin-left:8px; white-space:nowrap; }
        .rt-hwlink:hover { text-decoration:underline; }
        .rt-pahint { display:block; font-size:10.5px; color:var(--text-muted); margin-top:2px; }
        .rt-pahint.det { color:var(--info-text); font-weight:600; }
        .rt-pahint.err { color:var(--warning-text); }
        .rt-pa-basis { white-space:nowrap; }
        .rt-pa-scan { margin-left:6px; vertical-align:middle; }
        .rt-pa-banner { display:flex; gap:8px; align-items:flex-start; font-size:12px; color:var(--warning-text); background:var(--warning-soft); border:1px solid var(--warning-border); border-radius:var(--radius-sm); padding:8px 12px; margin:-4px 0 12px; }
        button.rt-kpi { font:inherit; text-align:left; cursor:pointer; color:inherit; }
        button.rt-kpi:hover { border-color:var(--border-strong); box-shadow:var(--shadow-card); }
        .rt-kpi.on { border-color:var(--accent); box-shadow:0 0 0 2px var(--accent-soft); }
        .rt-kpi.on .rt-kpi-s { color:var(--accent); }
        .rt-pa-filter { display:flex; align-items:center; gap:8px; flex-wrap:wrap; margin:0 0 10px; font-size:12px; color:var(--text-muted); }
        .rt-pa { margin-top:12px; }
        .rt-pa-grid { display:grid; grid-template-columns:repeat(auto-fit, minmax(220px, 1fr)); gap:12px; margin:10px 0; }
        .rt-pa-field { display:flex; flex-direction:column; gap:4px; font-size:11px; color:var(--text-muted); font-weight:600; }
        .rt-pa-field textarea, .rt-pa-field input[type=number] { font:inherit; font-size:12px; font-weight:400; color:var(--text); padding:6px 9px; border:1px solid var(--border); border-radius:var(--radius-sm); background:var(--card); box-sizing:border-box; width:100%; }
        .rt-pa-field textarea { min-height:64px; resize:vertical; }
        .rt-pa-nums { display:flex; gap:10px; flex-wrap:wrap; }
        .rt-pa-nums .rt-pa-field { width:150px; }
        .rt-pa-toggles { display:flex; flex-wrap:wrap; gap:6px 16px; margin:8px 0; font-size:12px; }
        .rt-pa-toggles label { display:flex; align-items:center; gap:6px; min-height:32px; cursor:pointer; }
        .rt-pa-foot { display:flex; align-items:center; gap:8px; flex-wrap:wrap; margin-top:10px; font-size:11px; }
        .rt-pa-help { font-size:11.5px; color:var(--text-muted); line-height:1.5; margin:4px 0 8px; }
        .rt-pa-status { font-size:11.5px; color:var(--text-muted); margin:2px 0 8px; }
        .rt-pa-pv { border-top:1px solid var(--border-soft); margin-top:12px; padding-top:12px; }
        .rt-pa-pv h4 { margin:12px 0 6px; font-size:11px; text-transform:uppercase; letter-spacing:.05em; color:var(--text-muted); font-weight:600; }
        .rt-pa-stats { display:flex; flex-wrap:wrap; gap:8px; }
        .rt-pa-stat { border:1px solid var(--border); border-radius:var(--radius-sm); padding:8px 12px; min-width:120px; background:var(--card); }
        .rt-pa-stat b { display:block; font-size:18px; font-variant-numeric:tabular-nums; color:var(--text); }
        .rt-pa-stat span { font-size:10.5px; color:var(--text-muted); }
        .rt-pa-tablewrap { overflow-x:auto; max-width:100%; }
        .rt-pa-table { width:100%; border-collapse:collapse; font-size:11.5px; }
        .rt-pa-table th { text-align:left; font-weight:500; color:var(--text-muted); border-bottom:1px solid var(--border); padding:5px 8px; white-space:nowrap; }
        .rt-pa-table td { border-bottom:1px solid var(--border-soft); padding:5px 8px; vertical-align:top; }
        .rt-pa-table td.num, .rt-pa-table th.num { text-align:right; font-variant-numeric:tabular-nums; }
        .rt-pa-v { display:inline-block; font-size:10px; font-weight:700; padding:1px 7px; border-radius:999px; background:var(--bg-subtle); color:var(--text-muted); white-space:nowrap; }
        .rt-pa-v.sent { background:var(--info-soft); color:var(--info-text); }
        .rt-pa-v.miss { background:var(--warning-soft); color:var(--warning-text); }
        @media (max-width: 640px) {
          .rt-pa-scan, .rt-pa-foot .btn, .rt-pa-filter .chip, .rt-pa .seg-tab, .rt-pa-use { min-height:36px; }
          .rt-pa-nums .rt-pa-field { width:calc(50% - 5px); }
          .rt-pa-basis { white-space:normal; }
          /* the actions wrap under the label so the label + pre-arrival hint keep the full width */
          .rt-item { flex-wrap:wrap; }
          .rt-imain { flex:1 1 calc(100% - 24px); }
          .rt-iacts { margin-left:18px; }
        }
      </style>
      ${this.headHtml()}
      ${this.genNote ? `<div class="rt-note">${this.esc(this.genNote)}</div>` : ''}
      ${dayView ? this.dayHtml({ detailBody, pill, kpi }) : `
      <div class="rt-basis">next ${this.board.days} days · as of ${this.esc(this.fmtStamp(this.board.as_of))}${this.boardLoading ? ' · <span class="rtd-busy">Updating…</span>' : ''}${this.paBasis(s)}</div>
      ${this.refreshErrorBar(this.boardError, this.boardLoading, this.fresh.isStale('board'), this.board.as_of)}
      ${this.paBanner(s)}
      <div class="rt-kpis">
        ${kpi(s.arrivals, 'Arrivals', `next ${this.board.days}d`)}
        ${kpi(s.verified, 'Verified', `of ${s.arrivals ?? 0} arrivals`)}
        ${kpi(s.at_risk, 'At risk', '<48h, unverified', 'red')}
        ${kpi(s.blocked, 'Blocked', 'failed item')}
        ${kpi(s.overdue_items, 'Overdue items', 'required, past due')}
        ${kpi(s.payment_flags ?? 0, 'Payment flags', '24h unpaid / 2nd due', (s.payment_flags ?? 0) > 0 ? 'red' : undefined)}
        ${this.paKpi(s)}
      </div>
      ${escalations.length
        ? `<div class="rt-esc-strip">${escalations.map(escCard).join('')}</div>`
        : `<div class="rt-allclear">All arrivals in the next 48h are verified ✓</div>`}
      ${this.paOnly ? this.paFilterBar(paRows.length) : ''}
      <div class="rt-board">
        ${paRows.length ? paRows.map(row).join('')
          : (this.paOnly && checks.length
            ? `<div class="rt-empty">Every arrival in the next ${this.esc(this.board.days)} days has its pre-arrival info sent ✓</div>`
            : `<div class="rt-empty">No upcoming arrivals tracked yet — hit “Refresh from reservations” to pull the next 30 days.</div>`)}
      </div>`}
      ${tplHtml}
      ${this.paAdminHtml()}`;
    this.bind();
  }

  // ── R114 Day view ────────────────────────────────────────────
  // The business day the page is on: the server's today (payload.today —
  // BUSINESS_TZ) once known; the browser's local day only before the first load.
  private dayToday(): string { return (this.dayData && this.dayData.today) || this.localToday(); }
  private shownDay(): string { return this.day || this.dayToday(); }
  private setDay(d: string | null): void {
    const today = this.dayToday();
    this.day = d && isDay(d) ? dayToStore(d, today) : null;
    this.writeStore(STORE_KEYS.day, this.day);
    this.draw();                       // the toolbar moves now; the rows show "Loading…" until /day answers
    void this.reloadActive();
  }
  private setView(v: DayView): void {
    if (v === this.view) return;
    this.view = v;
    this.writeStore(STORE_KEYS.view, v);
    if (v === 'board' && needsRefetch('board', this.refetchState())) {
      // R114 review: a stale board (edited from the Day view) is refetched; the
      // cached copy paints meanwhile with its note boxes locked.
      this.boardLoading = true;
      if (this.board) this.draw();
      else {
        const body = document.getElementById('rt-body');
        if (body) body.innerHTML = `<div style="padding:40px; text-align:center;">${LoadingSpinner.render()}</div>`;
      }
      void this.reloadBoard();
      return;
    }
    const refetch = v === 'day' && needsRefetch('day', this.refetchState());
    if (refetch) this.dayLoading = true;
    this.draw();
    if (refetch) void this.reloadActive();
  }
  // Housekeeping & Inspections hub, preselected tab (HubPage's one-shot hub_tab).
  private openHub(tab: string): void {
    try { sessionStorage.setItem('hub_tab', tab === 'inspections' ? 'inspections' : 'housekeeping'); } catch { /* private mode */ }
    document.dispatchEvent(new CustomEvent('app:navigate', { detail: 'hk-insp' }));
  }

  // Header: the view switch (Day | Upcoming) + the board's own controls.
  private headHtml(): string {
    const day = this.view === 'day';
    return `
      <style>
        .rt-head { row-gap:8px; }
        .rt-views .seg-tab { font-weight:600; }
        .rt-head-sp { flex:1 1 auto; }
        .rt-sub { font-size:12px; color:var(--text-muted); margin:2px 0 12px; }
        .rtd-bar { display:flex; align-items:center; gap:8px; flex-wrap:wrap; margin-bottom:10px; }
        .rtd-nav { display:inline-flex; gap:6px; }
        .rtd-nav .btn { min-width:36px; justify-content:center; }
        .rtd-date { width:auto; min-width:150px; flex:0 0 auto; }
        .rtd-when { font-size:13px; color:var(--text); white-space:nowrap; display:inline-flex; align-items:center; gap:6px; }
        .rtd-rel { font-size:11px; font-weight:600; color:var(--text-muted); background:var(--bg-subtle); border:1px solid var(--border); border-radius:var(--radius-pill); padding:1px 8px; }
        .rtd-rel.today { color:var(--accent); background:var(--accent-soft); border-color:var(--accent-border); }
        .rtd-busy { font-size:11px; color:var(--text-faint); }
        .rtd-spacer { flex:1 1 auto; }
        .rtd-modes { margin-bottom:12px; }
        .rtd-count { font-size:var(--fs-label); font-weight:700; background:var(--border-soft); color:var(--text-muted); border-radius:var(--radius-pill); padding:0 7px; font-variant-numeric:tabular-nums; }
        .seg-tab.active .rtd-count { background:var(--accent); color:#fff; }
        .rtd-kpis { display:grid; grid-template-columns:repeat(6, minmax(0, 1fr)); gap:10px; margin-bottom:14px; }
        .rtd-kpis .rt-kpi { min-width:0; }
        .rtd-list { display:flex; flex-direction:column; gap:8px; }
        .rtd-head { display:flex; flex-direction:column; gap:4px; padding:10px 14px 4px; }
        .rtd-head[data-toggle] { cursor:pointer; user-select:none; }
        .rtd-head[data-toggle]:hover { background:var(--bg-subtle); }
        .rtd-l1 { display:flex; align-items:center; gap:8px; flex-wrap:wrap; min-width:0; }
        .rtd-prop { font-size:13px; font-weight:600; color:var(--text); min-width:0; overflow-wrap:anywhere; }
        .rtd-l2 { display:flex; align-items:center; gap:6px; flex-wrap:wrap; font-size:12px; color:var(--text-muted); padding-left:20px; }
        .rtd-dot { color:var(--text-faint); }
        .rtd-ops { display:flex; align-items:center; gap:6px; flex-wrap:wrap; padding:4px 14px 10px 34px; }
        .rtd-time { display:inline-flex; align-items:center; gap:4px; font-size:11.5px; font-weight:700; padding:2px 9px; border-radius:var(--radius-pill); white-space:nowrap; background:var(--bg-subtle); color:var(--text); border:1px solid var(--border); font-variant-numeric:tabular-nums; }
        .rtd-time .rtd-kind { font-weight:600; color:var(--text-muted); }
        .rtd-time.eci { background:var(--warning-soft); color:var(--warning-text); border-color:var(--warning-border); }
        .rtd-time.lco { background:var(--info-soft); color:var(--info-text); border-color:var(--accent-border); }
        .rtd-time.eci .rtd-kind, .rtd-time.lco .rtd-kind { color:inherit; }
        .rtd-badge { font-size:9.5px; font-weight:800; letter-spacing:.04em; }
        .rtd-same { font-size:11px; font-weight:600; color:var(--accent-hover); background:var(--accent-soft); border-radius:var(--radius-pill); padding:2px 9px; white-space:nowrap; }
        .rtd-chip { display:inline-flex; align-items:center; gap:5px; font-size:11.5px; padding:3px 10px; border-radius:var(--radius-pill); border:1px solid var(--border); background:var(--card); color:var(--text); max-width:100%; line-height:1.4; }
        .rtd-chip.ok { background:var(--success-soft); color:var(--success-text); border-color:var(--success-border); }
        .rtd-chip.warn { background:var(--warning-soft); color:var(--warning-text); border-color:var(--warning-border); }
        .rtd-chip.risk { background:var(--danger-soft); color:var(--danger-text); border-color:var(--danger); }
        .rtd-chip.muted { background:var(--bg-subtle); color:var(--text-faint); }
        .rtd-tag { font-size:11px; font-weight:600; padding:3px 10px; border-radius:var(--radius-pill); border:1px solid var(--border); background:var(--card); color:var(--text-muted); cursor:pointer; white-space:nowrap; }
        .rtd-tag:hover { border-color:var(--border-strong); color:var(--text); }
        .rtd-tag.on.eci { background:var(--warning-soft); color:var(--warning-text); border-color:var(--warning); }
        .rtd-tag.on.lco { background:var(--info-soft); color:var(--info-text); border-color:var(--info); }
        .rtd-prog { font-size:11px; color:var(--text-muted); font-variant-numeric:tabular-nums; white-space:nowrap; }
        .rtd-gen { font-size:11.5px; padding:4px 11px; }
        .rtd-other { border:1px dashed var(--border); border-radius:10px; padding:10px 14px; margin-top:4px; background:var(--card); }
        .rtd-other-h { font-size:12px; font-weight:600; color:var(--text); margin-bottom:6px; }
        .rtd-other-list { display:flex; flex-wrap:wrap; gap:6px; }
        .rtd-loading { padding:28px; text-align:center; }
        .rtd-kpis.pa7 { grid-template-columns:repeat(7, minmax(0, 1fr)); }
        .rtd-kpis button.rt-kpi { display:flex; flex-direction:column; justify-content:flex-start; align-items:stretch; }   /* the tappable pre-arrival tile lines up with the div tiles */
        .rtd-warnbar { display:flex; gap:8px; align-items:center; flex-wrap:wrap; font-size:12px; color:var(--warning-text); background:var(--warning-soft); border:1px solid var(--warning-border); border-radius:var(--radius-sm); padding:8px 12px; margin:0 0 12px; }
        .rtd-warnbar .btn { margin-left:auto; }
        .rtd-syncmeta { white-space:nowrap; }
        .rtd-syncmeta.off { white-space:normal; }
        @media (max-width: 640px) {
          .rtd-kpis, .rtd-kpis.pa7 { grid-template-columns:repeat(3, minmax(0, 1fr)); gap:8px; }
          .rtd-kpis.pa7 > .rt-kpi:last-child { grid-column:1 / -1; }   /* the 7th tile (Pre-arrival not sent) runs full width */
          .rtd-kpis .rt-kpi { padding:8px 10px; }
          .rtd-kpis .rt-kpi-v { font-size:18px; }
          .rtd-kpis .rt-kpi:nth-child(-n+3) .rt-kpi-s { display:none; }   /* "arriving" / "departing" filler — keep the phone strip short */
          .rtd-l1 .rt-caret:empty { display:none; }
          .rtd-date { flex:1 1 140px; min-width:0; }
          .rtd-bar .btn, .rtd-tag, .rtd-ops .kb-btn, .rtd-modes .seg-tab, .rt-views .seg-tab, .rt-head .btn, .rtd-warnbar .btn, .rt-notewrap .rt-btn { min-height:36px; }
          .rtd-ops .kb-btn { min-width:36px; }
          .rtd-modes .seg-tabs { width:100%; }
          .rtd-modes .seg-tab { flex:1 1 auto; justify-content:center; padding:7px 8px; }
          .rtd-l2 { padding-left:0; }
          .rtd-ops { padding-left:14px; }
          .rtd-when { white-space:normal; }
        }
      </style>
      <div class="rt-head">
        <h2>Arrivals</h2>
        <div class="seg-tabs rt-views" role="tablist" aria-label="Arrivals view">
          <button type="button" role="tab" aria-selected="${day}" class="seg-tab ${day ? 'active' : ''}" data-rt-view="day">📅 Day</button>
          <button type="button" role="tab" aria-selected="${!day}" class="seg-tab ${day ? '' : 'active'}" data-rt-view="board">Upcoming</button>
        </div>
        <span class="rt-head-sp"></span>
        ${day ? '' : `
          <div class="seg-tabs" role="tablist" aria-label="Horizon">
            ${[7, 14, 30].map(d => `<button type="button" class="seg-tab ${this.days === d ? 'active' : ''}" data-days="${d}">${d}d</button>`).join('')}
          </div>
          <button type="button" class="btn btn-primary" data-gen="1" ${this.generating ? 'disabled' : ''}>${this.generating ? 'Refreshing…' : '↻ Refresh from reservations'}</button>`}
      </div>
      <div class="rt-sub">${day
        ? 'Every check-in and check-out on one day — the clean, the cleaner and the inspector behind each one.'
        : 'Pre-check-in verification — every upcoming arrival, checked off before the guest gets codes.'}</div>`;
  }

  // R114 fix2: a failed refetch is said out loud above the cached copy — never
  // a silent, editable stale copy. stale = the notes stay locked until a reload lands.
  private refreshErrorBar(err: string, loading: boolean, stale: boolean, asOf: string | null | undefined): string {
    if (!err || loading) return '';
    return `<div class="rtd-warnbar" role="alert"><span>⚠ Couldn’t refresh — ${this.esc(err)}. Showing the copy from ${this.esc(this.fmtStamp(asOf || null) || 'earlier')}${stale ? '; it may be out of date, so notes stay locked until it reloads' : ''}.</span><button type="button" class="btn btn-secondary btn-sm" data-rtd-refresh>Try again</button></div>`;
  }
  // R114 fix2: the checklist self-heal timer as this server runs it — the
  // basis-line text (cadence from the server, never a hard-coded 30) and a
  // warning bar when its last pass failed (a console.warn alone told nobody).
  private syncMeta(sh: DaySelfHeal | null | undefined, syncedAt: string | null | undefined): string {
    if (!sh) return syncedAt ? ` · <span class="rtd-syncmeta">checklists synced ${this.esc(this.relAgo(syncedAt))}</span>` : '';
    if (!sh.armed) return ` · <span class="rtd-syncmeta off" title="${this.esc(`Not armed: ${sh.reason || 'unknown'}`)}">automatic checklist sync is off on this server — new bookings get a checklist via ↻ Generate or the Upcoming view</span>`;
    const tip = `Checklists are created / re-dated from reservations automatically every ${sh.minutes} min (arrivals in the next ${sh.days} days)`;
    return ` · <span class="rtd-syncmeta" title="${this.esc(tip)}">${syncedAt ? `checklists synced ${this.esc(this.relAgo(syncedAt))}` : `checklists sync every ${this.esc(sh.minutes)} min (first pass pending)`}</span>`;
  }
  private syncBanner(sh: DaySelfHeal | null | undefined): string {
    if (!sh || !sh.armed || sh.last_ok !== false) return '';
    return `<div class="rt-pa-banner" role="status"><span>⚠</span><span>Automatic checklist sync failed ${this.esc(this.relAgo(sh.last_at))}${sh.last_error ? ` (${this.esc(sh.last_error)})` : ''} — new bookings and date changes may be missing here until it recovers. ↻ Generate on a row, or open Upcoming, to sync now.</span></div>`;
  }

  // The Day view body: toolbar, filter tabs, KPI strip, rows, other cleans.
  private dayHtml(h: { detailBody: (c: any) => string; pill: (r: string) => string; kpi: (v: any, label: string, sub: string, tone?: string) => string }): string {
    const p = this.dayData;
    const today = this.dayToday();
    const shown = this.shownDay();
    const lbl = dayLabel(shown, today);
    const min = (p && p.range && p.range.from) || addDays(today, -DAY_WINDOW);
    const max = (p && p.range && p.range.to) || addDays(today, DAY_WINDOW);
    const fresh = !!p && p.date === shown;          // the payload on hand IS the day on screen
    const counts = fresh ? modeCounts(p) : null;
    const c = (fresh && p ? p.counts : null) as any;
    const bar = `
      <div class="rtd-bar">
        <div class="rtd-nav">
          <button type="button" class="btn btn-secondary" data-rtd-nav="-1" aria-label="Previous day" title="Previous day" ${shown <= min ? 'disabled' : ''}>‹</button>
          <button type="button" class="btn btn-secondary" data-rtd-nav="0" title="Jump to today">Today</button>
          <button type="button" class="btn btn-secondary" data-rtd-nav="1" aria-label="Next day" title="Next day" ${shown >= max ? 'disabled' : ''}>›</button>
        </div>
        <input type="date" class="input rtd-date" data-rtd-date value="${this.esc(shown)}" min="${this.esc(min)}" max="${this.esc(max)}" aria-label="Pick a day">
        <span class="rtd-when"><b>${this.esc(lbl.long)}</b>${lbl.rel ? `<span class="rtd-rel ${lbl.rel === 'Today' ? 'today' : ''}">${this.esc(lbl.rel)}</span>` : ''}${this.dayLoading && fresh ? '<span class="rtd-busy">Updating…</span>' : ''}</span>
        <span class="rtd-spacer"></span>
        <button type="button" class="btn btn-ghost" data-rtd-refresh title="Reload this day" aria-label="Reload this day">↻</button>
      </div>
      <div class="rtd-modes">
        <div class="seg-tabs" role="tablist" aria-label="Show">
          ${MODES.map(m => { const on = this.mode === m && !this.paOnly; /* R114 fix2: the pre-arrival filter (arrivals only) replaces the tab — tapping a tab clears it */ return `<button type="button" role="tab" aria-selected="${on}" class="seg-tab ${on ? 'active' : ''}" data-rtd-mode="${m}">${MODE_LABELS[m]} <span class="rtd-count">${counts ? counts[m] : '…'}</span></button>`; }).join('')}
        </div>
      </div>`;
    if (!fresh) {
      const body = this.dayError && !this.dayLoading
        ? `<div class="rt-empty">Couldn't load ${this.esc(lbl.long)} — ${this.esc(this.dayError)}<div style="margin-top:10px;"><button type="button" class="btn btn-secondary" data-rtd-refresh>Try again</button></div></div>`
        : `<div class="rtd-loading">${LoadingSpinner.render()}</div>`;
      return bar + body;
    }
    const nIn = c.check_ins || 0, nOut = c.check_outs || 0;
    // null = that store could not be read this time (payload.unread) — "—", never a false zero
    const nv = (x: any) => (x == null ? '—' : x);
    const unk = (x: any) => x == null;
    // R114 review: pre-arrival auto-detect on the Day view too (the board's KPI / banner / basis helpers)
    const pa = p!.prearrival || null;
    const ps = pa ? { prearrival_scan: pa.scan, prearrival_pending: pa.pending, prearrival_pending_48h: pa.pending_48h, prearrival_detected: pa.detected, prearrival_untracked: pa.untracked, prearrival_past: !!pa.past } : null;
    const paTile = ps && ps.prearrival_pending != null ? this.paKpi(ps) : '';
    const inspSub = unk(c.inspections_total) ? 'couldn’t be read'
      : `${c.inspections_done || 0} inspected · ${c.inspections_unassigned || 0} without one${c.inspections_moved ? ` · ${c.inspections_moved} moved to another day` : ''}`;
    const kpis = `
      <div class="rt-kpis rtd-kpis ${paTile ? 'pa7' : ''}">
        ${h.kpi(nIn, 'Check-ins', unk(c.arrivals_not_generated) ? 'arriving' : (c.arrivals_not_generated ? `${c.arrivals_not_generated} without a checklist` : 'arriving'))}
        ${h.kpi(nOut, 'Check-outs', 'departing')}
        ${h.kpi(c.same_day_turns || 0, 'Same-day turns', 'out and in the same day')}
        ${h.kpi(unk(c.arrivals_verified) ? '—' : `${c.arrivals_verified}/${nIn}`, 'Arrivals verified', unk(c.arrivals_verified) ? 'checklists couldn’t be read' : `${c.arrivals_at_risk || 0} at risk · ${c.arrivals_blocked || 0} blocked`, (c.arrivals_at_risk || c.arrivals_blocked) ? 'red' : '')}
        ${h.kpi(unk(c.departures_turn_done) ? '—' : `${c.departures_turn_done}/${nOut}`, 'Cleans done', unk(c.departures_turn_done) ? 'cleans couldn’t be read' : `${c.departures_turn_unassigned || 0} unassigned · ${c.departures_no_turn || 0} not generated`, c.departures_turn_unassigned ? 'red' : '')}
        ${h.kpi(unk(c.inspections_total) ? '—' : `${nv(c.inspections_assigned)}/${c.inspections_total}`, 'Inspectors assigned', inspSub)}
        ${paTile}
      </div>`;
    // "Pre-arrival not sent" filter (the KPI tile toggles it): arrivals whose pre-arrival item is still pending
    const allRows = rowsFor(p, this.mode);
    const paRows = this.paOnly ? paRowsFor(p) : null;
    const rows = paRows || allRows;
    // R114 fix2 (lib paEmptyText): an all-clear only when every arrival's pre-arrival status is known
    const list = rows.length
      ? rows.map(r => (r.kind === 'arrival' ? this.arrivalRowHtml(r as DayArrival, h) : this.departureRowHtml(r as DayDeparture))).join('')
      : `<div class="rt-empty">${this.esc(this.paOnly ? paEmptyText(p, lbl.long) : emptyText(this.mode, lbl.long))}</div>`;
    const others = this.paOnly ? [] : (p!.other_cleans || []);
    const otherHtml = this.mode !== 'in' && others.length ? `
      <div class="rtd-other">
        <div class="rtd-other-h">Also cleaning ${lbl.rel === 'Today' ? 'today' : `on ${this.esc(lbl.long)}`}: ${others.length} <span class="rt-faint" style="font-weight:400;">(moved from another day, or added by hand — no checkout here)</span></div>
        <div class="rtd-other-list">${others.map(t => `<span class="rtd-chip ${t.at_risk ? 'risk' : t.done ? 'ok' : t.unassigned ? 'warn' : ''}">🧹 <b>${this.esc(t.listing_name)}</b> · ${t.reason === 'moved' && t.moved_from ? `moved from ${this.esc(longDay(t.moved_from))}` : this.esc(t.type === 'turnover' ? 'extra clean' : t.type.replace(/_/g, ' '))} · ${t.unassigned ? 'unassigned' : this.esc(t.cleaners.join(', '))} · ${this.esc(turnStatusLabel(t.status))}</span>`).join('')}</div>
      </div>` : '';
    const oi = this.paOnly ? [] : (p!.other_inspections || []);
    const otherInspHtml = this.mode !== 'in' && oi.length ? `
      <div class="rtd-other">
        <div class="rtd-other-h">Also inspecting ${lbl.rel === 'Today' ? 'today' : `on ${this.esc(lbl.long)}`}: ${oi.length} <span class="rt-faint" style="font-weight:400;">(moved from another day, or scheduled by hand — no checkout here)</span></div>
        <div class="rtd-other-list">${oi.map(x => this.otherInspChipHtml(x)).join('')}</div>
      </div>` : '';
    const unread = p!.unread || [];
    const what = [unread.includes('rt_checks') ? 'checklists' : '', unread.includes('ao_turns') || unread.includes('ao_cleaners') ? 'cleans' : '',
      unread.some(k => /^ao_inspection/.test(k)) ? 'inspections' : ''].filter(Boolean);
    const unreadBar = unread.length
      ? `<div class="rtd-warnbar" role="status"><span>⚠ ${this.esc(what.join(', ') || 'some details')} couldn’t be read just now — those counts show “—”, nothing is missing.</span><button type="button" class="btn btn-secondary btn-sm" data-rtd-refresh>Retry</button></div>` : '';
    const synced = this.syncMeta(p!.self_heal, p!.checks_synced_at);
    const basis = `<div class="rt-basis">${this.esc(p!.basis || '')} · as of ${this.esc(this.fmtStamp(p!.as_of))}${synced}${this.paBasis(ps)}</div>`;
    const errBar = this.refreshErrorBar(this.dayError, this.dayLoading, this.fresh.isStale('day'), p!.as_of);
    return bar + errBar + unreadBar + this.syncBanner(p!.self_heal) + this.paBanner(ps) + kpis + (this.paOnly ? this.paFilterBar(rows.length, { past: !!(pa && pa.past), untracked: pa ? pa.untracked : null }) : '') + `<div class="rtd-list">${list}</div>` + otherHtml + otherInspHtml + basis;
  }
  // One "Also inspecting" chip — a job moved here from another checkout, or
  // scheduled by hand; a missed one rolled into today says so.
  private otherInspChipHtml(x: OtherInspection): string {
    const cls = x.state === 'done' ? 'ok' : x.state === 'unassigned' ? 'warn' : x.state === 'skipped' ? 'muted' : (x.missed ? 'risk' : (x.orphaned ? 'warn' : ''));
    const why = x.type === 'moved' ? (x.moved_from ? `moved from ${this.esc(longDay(x.moved_from))}` : 'rescheduled') : '📌 scheduled by hand';
    const missed = x.missed ? ` · <b>missed ${this.esc(longDay(x.date))}</b>` : '';
    // R114 fix3: walked here before the guest left — the check after checkout is still owed on `due`
    const walked = x.walked_early ? ` · <span title="Walked while the guest was still in — the inspection after checkout is still owed${x.due ? ` on ${this.esc(longDay(x.due))}` : ''}">👣 walked · after-checkout check ${x.due ? this.esc(longDay(x.due)) : 'still owed'}</span>` : '';
    // R114 review: its booking was cancelled / re-dated (the queue's ⚠ flag)
    const orphan = x.orphaned ? ' · <b title="No stay checks out on its original date any more — the booking was cancelled or its dates changed. Cancel it from Inspections (⋯) if it’s not needed.">⚠ booking changed</b>' : '';
    return `<span class="rtd-chip ${cls}">🔍 <b>${this.esc(x.listing_name)}</b> · ${why}${missed}${orphan}${walked} · ${x.inspector ? this.esc(x.inspector) : 'unassigned'} · ${this.esc(inspectionLabel(x.state))}${x.state === 'done' ? ' ✓' : ''}</span>`;
  }

  // The cleaner chip shared by an arrival's ready clean and a departure's clean.
  private turnChipHtml(t: TurnLite, prefix: string): string {
    const names = t.cleaners.length ? this.esc(t.cleaners.join(', ')) : '';
    const cls = t.at_risk ? 'risk' : t.done ? 'ok' : t.unassigned ? 'warn' : '';
    const who = t.unassigned ? '<b>⚠ Unassigned</b>' : names;
    return `<span class="rtd-chip ${cls}" title="${t.at_risk ? 'Same-day clean, not done yet' : 'Housekeeping turn'}">🧹 ${prefix}${who} · ${this.esc(t.done ? `${turnStatusLabel(t.status)} ✓` : turnStatusLabel(t.status))}</span>`;
  }
  private timeChipHtml(kind: 'arrival' | 'departure', time: { label: string; source: string }): string {
    const flag = kind === 'arrival' ? time.source === 'eci' : time.source === 'lco';
    const tip = time.source === 'listing' ? `Listing ${kind === 'arrival' ? 'check-in' : 'check-out'} time`
      : time.source === 'default' ? `No ${kind === 'arrival' ? 'check-in' : 'check-out'} time on the listing — house default`
      : kind === 'arrival' ? 'Early check-in time' : 'Late check-out time';
    return `<span class="rtd-time ${flag ? (kind === 'arrival' ? 'eci' : 'lco') : ''}" title="${this.esc(tip)}">${kind === 'arrival' ? '🛬' : '🛫'} <span class="rtd-kind">${kind === 'arrival' ? 'Check-in' : 'Check-out'}</span> ${this.esc(time.label)}${flag ? ` <span class="rtd-badge">${kind === 'arrival' ? 'ECI' : 'LCO'}</span>` : ''}</span>`;
  }
  private metaHtml(r: DayArrival | DayDeparture): string {
    const bits = [`<b style="color:var(--text); font-weight:600;">${this.esc(r.guest_first)}</b>`];
    const party = partyLabel(r.party_size), nights = nightsLabel(r.nights);
    if (party) bits.push(this.esc(party));
    if (nights) bits.push(this.esc(nights));
    return `<div class="rtd-l2">${bits.join('<span class="rtd-dot">·</span>')}
      <span class="rt-chan" style="text-transform:none;">${this.esc(r.channel_label)}</span>
      ${r.owner_stay ? '<span class="pill pill-purple">👤 Owner stay</span>' : ''}</div>`;
  }

  private arrivalRowHtml(a: DayArrival, h: { detailBody: (c: any) => string; pill: (r: string) => string }): string {
    const c = a.check;
    const open = !!c && this.expanded.has(String(c.id));
    const pay = a.payment_flag
      ? `<span class="pill ${a.payment_severity === 'emergency' ? 'pill-red' : 'pill-amber'}" title="${a.payment_flag === 'unpaid_24h' ? 'Booked 24h+ ago with no payment collected — see the Issues board' : '2nd payment due — inside 2 weeks of check-in and not fully paid'}">💳 ${a.payment_flag === 'unpaid_24h' ? 'UNPAID 24h+' : '2nd payment due'}</span>` : '';
    const unknown = a.check_state === 'unknown';
    const status = c ? h.pill(String(c.rollup || 'pending'))
      : `<span class="rt-pill" style="background:var(--bg-subtle); color:var(--text-muted);">${unknown ? 'Checklist unavailable' : 'No checklist'}</span>`;
    const ready = a.ready_turn ? this.turnChipHtml(a.ready_turn, 'Clean: ') : '';
    const genBtn = (label: string) => `<button type="button" class="btn btn-secondary rtd-gen" data-rtd-gen="${generateDays(a.check_in || this.shownDay(), this.dayToday())}" ${this.generating ? 'disabled' : ''}>${this.generating ? 'Generating…' : label}</button>`;
    // R114 review: "arrived before tracking" only before the first checklist ever made
    const since = this.dayData && this.dayData.tracking_since;
    const pastWhy = since && a.check_in && a.check_in >= since ? 'not generated before arrival' : 'arrived before tracking';
    // R114 fix2: an automatic resync is promised only when this server's timer is armed and covers the day
    const sh = this.dayData && this.dayData.self_heal;
    const auto = autoSyncCovers(sh, a.check_in, this.dayToday());
    const checklist = c
      ? `<span class="rtd-prog">${c.required_done ?? 0}/${c.required_total ?? 0} required</span>${c.dates_stale
        ? `<span class="rtd-chip warn" title="${this.esc(datesChangedTip(c, { auto, minutes: sh ? sh.minutes : undefined }))}">📅 Dates changed</span>${a.generatable ? genBtn('↻ Sync') : ''}` : ''}`
      : unknown
        ? '<span class="rtd-chip muted">Checklist couldn’t be read — retry</span>'
        : a.generatable
          ? `<span class="rtd-chip muted" title="${this.esc(auto && sh ? `New bookings get their checklist automatically within ${sh.minutes} min — or Generate it now` : 'Generate it now (automatic sync does not cover this day on this server)')}">Checklist not generated</span>${genBtn('↻ Generate')}`
          : `<span class="rtd-chip muted">No checklist (${a.check_in && a.check_in < this.dayToday() ? pastWhy : 'beyond the 60-day window'})</span>`;
    const eci = `<button type="button" class="rtd-tag eci ${a.eci.on ? 'on' : ''}" data-rt-ecilco="${this.esc(a.listing_id)}|${this.esc(a.check_in || '')}|eci|${a.eci.on ? 1 : 0}" title="Early check-in — tap to toggle; feeds the HK + inspection tasks">⏰ ECI${a.eci.on && a.eci.time ? ` ${this.esc(a.eci.time)}` : ''}</button>`;
    const menu = a.ready_turn ? kebabHtml([
      { label: 'Open in Housekeeping', icon: '🧹', attrs: { 'data-rtd-open': 'housekeeping' } },
    ], { tip: 'More' }) : '';
    return `
      <div class="rt-row rtd-row" id="rtd-${this.esc(a.id)}">
        <div class="rtd-head" ${c ? `data-toggle="${this.esc(c.id)}" aria-expanded="${open}"` : ''}>
          <div class="rtd-l1">
            <span class="rt-caret">${c ? (open ? '▾' : '▸') : ''}</span>
            ${this.timeChipHtml('arrival', a.time)}
            <span class="rtd-prop">${this.esc(a.listing_name)}</span>
            ${a.same_day ? '<span class="rtd-same" title="A guest checks out of this home the same day">↔ Same-day turn</span>' : ''}
            ${pay}
            <span class="rtd-spacer"></span>
            ${status}
          </div>
          ${this.metaHtml(a)}
        </div>
        <div class="rtd-ops">${ready}${checklist}${eci}${menu}</div>
        ${open && c ? h.detailBody(c) : ''}
      </div>`;
  }

  private departureRowHtml(d: DayDeparture): string {
    const t = d.turn;
    const turn = d.turn_match === 'unknown'
      ? '<span class="rtd-chip muted" title="The cleaning schedule couldn’t be read just now">🧹 Clean unavailable</span>'
      : !t
        ? '<span class="rtd-chip muted" title="Turns generate automatically 14 days ahead">🧹 No clean generated yet</span>'
        : d.turn_match === 'moved'
          ? `<span class="rtd-chip warn">🧹 Clean moved to ${this.esc(longDay(t.date || ''))} · ${t.unassigned ? 'unassigned' : this.esc(t.cleaners.join(', '))} · ${this.esc(turnStatusLabel(t.status))}</span>`
          : this.turnChipHtml(t, '');
    const i = d.inspection;
    // R114 review: a rescheduled inspection says where it went (like the moved clean beside it) — never "No inspector"
    const insp = i.state === 'moved'
      ? `<span class="rtd-chip warn" title="This checkout's inspection was rescheduled — it shows under “Also inspecting” on its new day">🔍 Inspection moved to ${this.esc(longDay(i.moved_to || ''))} · ${i.inspector ? this.esc(i.inspector) : 'unassigned'}</span>`
      : i.state === 'unknown'
        ? '<span class="rtd-chip muted" title="Inspection assignments couldn’t be read just now">🔍 Inspector unavailable</span>'
        : `<span class="rtd-chip ${i.state === 'done' ? 'ok' : i.state === 'unassigned' ? 'warn' : i.state === 'skipped' ? 'muted' : ''}" title="${i.walked_early ? `Moved earlier and walked on ${this.esc(longDay(i.walked_early))} while the guest was still in — the inspection after this checkout is still owed here` : 'Inspection for this checkout'}">🔍 ${i.inspector ? `${this.esc(i.inspector)} · ` : ''}${this.esc(i.state === 'unassigned' ? 'No inspector' : inspectionLabel(i.state))}${i.state === 'done' ? ' ✓' : ''}${i.moved_from ? ` · also covers ${this.esc(longDay(i.moved_from))}` : ''}${i.walked_early ? ` · 👣 walked ${this.esc(longDay(i.walked_early))} · after-checkout check` : ''}</span>`;
    const lco = `<button type="button" class="rtd-tag lco ${d.lco.on ? 'on' : ''}" data-rt-ecilco="${this.esc(d.listing_id)}|${this.esc(d.check_out || '')}|lco|${d.lco.on ? 1 : 0}" title="Late check-out — tap to toggle; feeds the HK + inspection tasks">🌙 LCO${d.lco.on && d.lco.time ? ` ${this.esc(d.lco.time)}` : ''}</button>`;
    const menu = kebabHtml([
      { label: 'Open in Housekeeping', icon: '🧹', attrs: { 'data-rtd-open': 'housekeeping' } },
      { label: 'Open Inspections', icon: '🔍', attrs: { 'data-rtd-open': 'inspections' } },
    ], { tip: 'More' });
    return `
      <div class="rt-row rtd-row" id="rtd-${this.esc(d.id)}">
        <div class="rtd-head">
          <div class="rtd-l1">
            <span class="rt-caret"></span>
            ${this.timeChipHtml('departure', d.time)}
            <span class="rtd-prop">${this.esc(d.listing_name)}</span>
            ${d.same_day ? `<span class="rtd-same" title="The next guest arrives the same day">↔ Same-day${d.next_arrival_time ? ` — next guest ${this.esc(d.next_arrival_time)}` : ''}</span>` : ''}
          </div>
          ${this.metaHtml(d)}
        </div>
        <div class="rtd-ops">${turn}${insp}${lco}${menu}</div>
      </div>`;
  }

  // One handler per data-rt-view / data-rtd-* attribute rendered above.
  private bindDay(): void {
    bindKebabs(this.container);
    const all = <T extends HTMLElement = HTMLElement>(sel: string) => this.container.querySelectorAll<T>(sel);
    all('[data-rt-view]').forEach(el => el.addEventListener('click', () => this.setView(el.dataset.rtView === 'board' ? 'board' : 'day')));
    all('[data-rtd-nav]').forEach(el => el.addEventListener('click', () => {
      const n = Number(el.dataset.rtdNav) || 0;
      if (!n) { this.setDay(null); return; }
      const next = addDays(this.shownDay(), n);
      if (inWindow(next, this.dayToday())) this.setDay(next);
    }));
    // R114 review: Chromium / Safari fire `change` on EVERY keystroke in a date
    // field — committing there redrew #rt-body mid-entry (focus lost, "21" →
    // the 2nd) or snapped a half-typed year back. Typed entry commits on Enter
    // or blur; a pick from the calendar popup still commits at once.
    all<HTMLInputElement>('[data-rtd-date]').forEach(el => {
      const guard = dateEntryGuard();   // lib/arrivalsDayView.mjs (tested)
      const commit = () => {
        const v = String(el.value || '');
        if (v === this.shownDay()) return;                 // no-op (also absorbs a blur right after Enter)
        if (isDay(v) && inWindow(v, this.dayToday())) this.setDay(v);
        else el.value = this.shownDay();                    // outside the ±180-day window: snap back — only once typing ends
      };
      el.addEventListener('keydown', (ev) => { if (guard.keydown(ev.key)) { ev.preventDefault(); commit(); } });
      el.addEventListener('pointerdown', () => { guard.pointerdown(); });   // the picker path commits through `change`
      el.addEventListener('change', () => { if (guard.change()) commit(); });
      el.addEventListener('blur', () => { if (guard.blur()) commit(); });
    });
    all('[data-rtd-mode]').forEach(el => el.addEventListener('click', () => {
      this.mode = parseMode(el.dataset.rtdMode);
      this.writeStore(STORE_KEYS.mode, this.mode);
      this.paOnly = false;   // R114 fix2: a tab replaces the (arrivals-only) pre-arrival filter
      this.draw();
    }));
    all('[data-rtd-refresh]').forEach(el => el.addEventListener('click', () => { void this.reloadActive(); }));
    all('[data-rtd-gen]').forEach(el => el.addEventListener('click', (ev) => {
      ev.stopPropagation();
      void this.generate(Number(el.dataset.rtdGen) || 30);
    }));
    all('[data-rtd-open]').forEach(el => el.addEventListener('click', (ev) => {
      ev.stopPropagation();
      this.openHub(el.dataset.rtdOpen || 'housekeeping');
    }));
  }

  // ── bind — one handler per data-* attribute rendered above ───
  private bind(): void {
    this.bindDay();   // R114: view switch + Day toolbar / rows
    // data-days → horizon segment
    this.container.querySelectorAll<HTMLElement>('[data-days]').forEach(el => el.addEventListener('click', () => {
      this.days = Number(el.dataset.days) || 14;
      void this.reloadBoard();
    }));
    // data-gen → POST /generate then reload
    this.container.querySelectorAll<HTMLElement>('[data-gen]').forEach(el => el.addEventListener('click', () => {
      void this.generate();
    }));
    // data-toggle → expand/collapse a board row
    this.container.querySelectorAll<HTMLElement>('[data-toggle]').forEach(el => el.addEventListener('click', () => {
      const id = el.dataset.toggle || '';
      if (this.expanded.has(id)) this.expanded.delete(id); else this.expanded.add(id);
      this.draw();
    }));
    // R35 #3: manual ECI/LCO tag from the arrivals board — shared store with
    // the HK board + inspection queue. stopPropagation so the row doesn't toggle.
    this.container.querySelectorAll<HTMLElement>('[data-rt-ecilco]').forEach(el => el.addEventListener('click', async (e) => {
      e.stopPropagation();
      const [listing_id, date, kind, cur] = String(el.dataset.rtEcilco || '').split('|');
      if (!listing_id || !date || !kind) return;
      const r = await fetch('/api/assetops/eci-lco', {
        method: 'POST', headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ listing_id, date, kind, value: cur !== '1' }),
      }).then(x => x.ok ? x.json() : null).catch(() => null);
      if (!r) { alert('Could not save the tag — try again.'); return; }
      this.markOtherViewStale();
      await this.reloadActive();
    }));
    // (R18 #11) escalation cards now expand inline via the shared data-toggle
    // handler above — no scroll-jump to the board row.
    // data-istate (+ data-id, data-key) → set an item's state
    this.container.querySelectorAll<HTMLElement>('[data-istate]').forEach(el => el.addEventListener('click', (ev) => {
      ev.stopPropagation();
      void this.setItemState(el.dataset.id || '', el.dataset.key || '', el.dataset.istate || '');
    }));
    // data-itemphoto → attach a verification screenshot to one checklist item
    this.container.querySelectorAll<HTMLInputElement>('[data-itemphoto]').forEach(el => el.addEventListener('change', async (ev) => {
      ev.stopPropagation();
      const [id, key] = String(el.dataset.itemphoto || '').split('|');
      const file = el.files && el.files[0];
      if (!id || !key || !file) return;
      this.uploadingItem = `${id}|${key}`; this.draw();
      try {
        const fd = new FormData(); fd.append('key', key); fd.append('photo', file, file.name);
        const r = await fetch(`${API}/checks/${encodeURIComponent(id)}/item-photo`, { method: 'POST', body: fd });
        if (r.ok) { this.uploadingItem = null; this.markOtherViewStale(); await this.reloadActive(); return; }
      } catch (_) { /* fall through */ }
      this.uploadingItem = null; this.draw();
    }));
    // R48 check-level note: keystrokes mirror into noteDrafts (no redraw —
    // keeps focus); save POSTs then reloads the board.
    this.container.querySelectorAll<HTMLTextAreaElement>('[data-noteid]').forEach(el => {
      el.addEventListener('click', ev => ev.stopPropagation());
      el.addEventListener('input', () => { this.noteDrafts.set(el.dataset.noteid || '', el.value); });
    });
    // R51 Quo: expand → lazy-fetch the guest's call/text history.
    this.container.querySelectorAll<HTMLElement>('[data-quo-toggle]').forEach(el => el.addEventListener('click', async (ev) => {
      ev.stopPropagation();
      const id = el.dataset.quoToggle || '';
      const phone = el.dataset.quoPhone || '';
      if (this.quoData.has(id)) { this.quoData.delete(id); this.draw(); return; }   // collapse
      this.quoData.set(id, 'loading'); this.draw();
      const j = await fetch(`/api/quo/guest-activity?phone=${encodeURIComponent(phone)}`).then(r => r.ok ? r.json() : null).catch(() => null);
      // Review fix: the user may have collapsed the section mid-flight — a
      // stale response must not silently reopen it.
      if (this.quoData.get(id) !== 'loading') return;
      this.quoData.set(id, j || { error: true });
      this.draw();
    }));
    this.container.querySelectorAll<HTMLElement>('[data-notesave]').forEach(el => el.addEventListener('click', async (ev) => {
      ev.stopPropagation();
      const id = el.dataset.notesave || '';
      if (!id || this.noteSaving) return;
      // Review fix: an escalated check renders detailBody (and this textarea)
      // TWICE — strip + board row. The draft mirrors every keystroke from
      // whichever copy the user typed in, so it is the truth; a container-wide
      // querySelector always found the STRIP copy and silently saved its stale
      // value, deleting the draft (the only copy of the typed note).
      const ta = this.container.querySelector<HTMLTextAreaElement>(`textarea[data-noteid="${id}"]`);
      const note = String(this.noteDrafts.has(id) ? this.noteDrafts.get(id) : (ta ? ta.value : '')).slice(0, 2000);
      this.noteSaving = id; this.draw();
      const r = await this.post(`/checks/${encodeURIComponent(id)}/note`, { note });
      this.noteSaving = null;
      if (r) {
        this.noteDrafts.delete(id);
        this.patchNote(id, r.check && r.check.note != null ? String(r.check.note) : note);   // R114 fix2: right even if the reload fails
        this.markOtherViewStale();
        await this.reloadActive();
      } else { this.draw(); }
    }));
    // data-tpltoggle → open/close the template editor
    this.container.querySelectorAll<HTMLElement>('[data-tpltoggle]').forEach(el => el.addEventListener('click', () => {
      this.tplOpen = !this.tplOpen;
      this.draw();
    }));
    // data-tf + data-ti → STATE-BACKED template inputs: mirror every keystroke
    // into tplItems so redraws never lose edits. No redraw here (keeps focus).
    this.container.querySelectorAll<HTMLInputElement>('[data-tf]').forEach(el => el.addEventListener('input', () => {
      const i = Number(el.dataset.ti);
      const f = el.dataset.tf || '';
      const it = this.tplItems[i];
      if (!it) return;
      if (f === 'offset_hours') it.offset_hours = Math.max(0, Math.min(720, Math.round(Number(el.value) || 0)));
      else if (f === 'label') it.label = el.value;
      this.tplDirty = true;
    }));
    // data-treq (+ data-ti) → toggle required/optional
    this.container.querySelectorAll<HTMLElement>('[data-treq]').forEach(el => el.addEventListener('click', () => {
      const it = this.tplItems[Number(el.dataset.ti)];
      if (!it) return;
      it.required = it.required === 'required' ? 'optional' : 'required';
      this.tplDirty = true; this.draw();
    }));
    // data-tch (+ data-ti) → toggle a channel flag (none selected = all)
    this.container.querySelectorAll<HTMLElement>('[data-tch]').forEach(el => el.addEventListener('click', () => {
      const it = this.tplItems[Number(el.dataset.ti)];
      const ch = el.dataset.tch || '';
      if (!it || !ch) return;
      let list = it.channels ? it.channels.slice() : [];
      if (list.includes(ch)) list = list.filter(c => c !== ch); else list.push(ch);
      it.channels = list.length ? list : null;
      this.tplDirty = true; this.draw();
    }));
    // data-tdel (+ data-ti) → remove a template item
    this.container.querySelectorAll<HTMLElement>('[data-tdel]').forEach(el => el.addEventListener('click', () => {
      const i = Number(el.dataset.ti);
      if (!(i >= 0 && i < this.tplItems.length)) return;
      this.tplItems.splice(i, 1);
      this.tplDirty = true; this.draw();
    }));
    // data-tadd → append a blank template item (stable generated key)
    this.container.querySelectorAll<HTMLElement>('[data-tadd]').forEach(el => el.addEventListener('click', () => {
      this.tplItems.push({
        key: `item_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 5)}`,
        label: '', description: '', required: 'required', offset_hours: 48, channels: null,
      });
      this.tplDirty = true; this.tplOpen = true; this.draw();
    }));
    // data-tsave → POST /template
    this.container.querySelectorAll<HTMLElement>('[data-tsave]').forEach(el => el.addEventListener('click', () => {
      void this.saveTemplate();
    }));
    this.bindPa();   // R114
  }
}
